import React, {
  Suspense,
  useEffect,
  useMemo,
  useCallback,
  useDeferredValue
} from 'react';
import styles from './Roadmap.module.scss';
import filterStyles from './FilterDropdown.module.scss';
import {
  Grid,
  View,
  Heading,
  Text,
  Flex,
  Image,
  Button,
  Switch,
  Provider as ProviderV3,
  defaultTheme,
  DialogTrigger,
  Dialog,
  ActionButton,
  Divider,
  Content,
  ButtonGroup,
  Tooltip,
  TooltipTrigger,
  MenuTrigger,
  Menu,
  Item,
  ToastQueue
} from '@adobe/react-spectrum';

import moment from 'moment';
import expandIcon from '../../../images/expand-icon.svg';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Wait from '@react/react-spectrum/Wait';
import {
  selectCategories,
  selectCompact,
  selectDateRange,
  selectError,
  selectFullScreenMode,
  selectLoading,
  selectParkingLot,
  selectProductGroups,
  selectAudiences,
  selectReleases,
  selectEvents,
  selectUnNestAll,
  selectFeatures,
  selectProductFilter,
  selectCollapseAll,
  selectFilteredReleases,
  selectSearchFilterActive,
  selectSearchFilteredItems,
  selectLanesTiers,
  selectGenAiFilter,
  selectRevenueImpactFilter,
  selectStatus,
  selectMomentTypeFilter,
  selectHighlightedIssue, // ✅ Add this to track single-click highlights
  selectHighlightSource, // ✅ Add this to track highlight source
  selectHideEmptySwimLanes, // ✅ Add this to hide empty swimlanes for Must Win filters
  selectMustWinView, // ✅ Track Must Wins view state
  selectHome
} from 'app/pages/Home/slice/selectors';

import { NoResults } from 'app/components/NoResults';
import { Legend } from './Legend';
import { Row } from './Row';
import { ParkingLot } from './ParkingLot';
import { PARKING_LOT_GRID_COLUMN_PX } from './parkingLotLayoutConstants';
import { MustWinsView } from '../MustWinsView/MustWinsView';
import { ThemeContext } from 'context/themeContext';
import { ThemeContextType } from 'types/theme';
import { AlertsContainer } from '../AlertsAndNotification/components/AlertsContainer';
import { useOverdueFeatureAlerts } from './hooks/useOverdueFeatureAlerts';
import collapseAllIcon from '../../../images/collapse-all.svg';
import unNestAllIcon from '../../../images/un-nest-all.svg';
import expandAllIcon from '../../../images/expand-all.svg';
import nestAllIcon from '../../../images/nest-all.svg';
import LeftSidebar from './LeftSidebar';
import { selectLayout } from 'app/pages/Layout/slice/selectors';
import ExpandIcon from '../../../images/expand-icon.svg';
import CollapseIcon from '../../../images/collapse-icon.svg';
import {
  saveUserPreferences,
  getUserPreferences,
  cleanupOldShowKeyDatesPreference,
  exitCompactModeAndRestoreTimeline,
  getEffectiveStatus,
  normalizeStatus
} from 'utils/helpers';
import { parseViewStateFromSearchParams } from 'utils/viewStateDeepLink';
import {
  shouldPreferUrlOverLocalPreferences,
  shouldSkipPreferencePersistence
} from 'utils/sharedViewMode';
import { RISK_LEVELS, STATUS_VALUES } from 'utils/constants';
import { Selection } from '@react-types/shared';
import type { RootState } from 'types';
import { useRsdFormsSlice } from 'app/components/RsdForms/store/slice';
import { subProjectMatchesMomentTypeSelection } from 'app/components/Header/momentTypeRoadmapFilter';

interface Quarter {
  id: number;
  label: string;
  months: string[];
  displayLabel: string;
}

/**
 * True if the given release list has at least one non-Key-Dates/Events release
 * with at least one project card. Shared by hasNoData and myStuffHasNoCards.
 */
function hasMainReleases(releasesList: any[] | undefined | null): boolean {
  const mainReleases = releasesList?.filter(
    release => release.type !== 'Key Dates' && !release.type.includes('Events')
  );

  return Boolean(
    mainReleases &&
      mainReleases.length > 0 &&
      mainReleases.some(
        release =>
          release.projects &&
          release.projects.length > 0 &&
          release.projects.some(
            project => project.project && project.project.length > 0
          )
      )
  );
}
const Roadmap: React.FC = () => {
  const [expandedQuarters, setExpandedQuarters] = React.useState<string[]>([]);
  const [keyDatesRelease, setKeyDatesRelease] = React.useState<any>();
  const [quarters, setQuarters] = React.useState<Quarter[]>([]);
  const [todayDetails, setTodayDetails] = React.useState({
    currentMonthIndex: -1,
    currentQuarterIndex: -1,
    currentDate: -1
  });
  const [selectedFilterOptions, setSelectedFilterOptions] =
    React.useState<Selection>(new Set());

  // State for gap filler width
  const [gapWidth, setGapWidth] = React.useState<number>(0);

  // Add tooltip state management similar to TimelineCard
  const [openTooltip, setOpenTooltip] = React.useState<string | null>(null);
  const tooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Track if initial data load is complete
  const initialLoadComplete = React.useRef<boolean>(false);

  // Timer for debouncing the no data toast
  const noDataAlertTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Check if slideout is open by checking URL path
  const location = useLocation();
  const isSlideoutOpen = location.pathname.includes('/moments/');

  // Add tooltip handlers similar to TimelineCard
  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  const { theme, changeTheme } = React.useContext(
    ThemeContext
  ) as ThemeContextType;
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  useRsdFormsSlice();
  // Compute overdue-feature alerts (CSME-112700) from roadmap release data and publish them to
  // the AlertsAndNotification slice so AlertsContainer surfaces them alongside server-loaded
  // alerts, one per overdue feature (no aggregation) so each is independently navigable.
  useOverdueFeatureAlerts();
  const releases = useSelector(selectReleases);
  const filteredReleases = useSelector(selectFilteredReleases);
  const categories = useSelector(selectCategories);
  const productGroups = useSelector(selectProductGroups);
  const audiences = useSelector(selectAudiences);
  const selectedEvents = useSelector(selectEvents);
  const selectedFeatures = useSelector(selectFeatures);
  const selectedProducts = useSelector(selectProductFilter);
  const genAiFilter = useSelector(selectGenAiFilter);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const hideEmptySwimLanes = useSelector(selectHideEmptySwimLanes);
  const isCompact = useSelector(selectCompact);
  // Stable ref so loadPreferencesAndSetState (below) can read the current
  // Redux isCompact value without adding it as an effect dependency.
  const isCompactRef = React.useRef(isCompact);
  isCompactRef.current = isCompact;
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  const showParkingLot = useSelector(selectParkingLot);
  const dateRange = useSelector(selectDateRange);
  const isFullScreenMode = useSelector(selectFullScreenMode);
  const isUnNested = useSelector(selectUnNestAll);
  const { isSidebarOpen } = useSelector(selectLayout);
  const isCollapsed = useSelector(selectCollapseAll);
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const searchFilteredItems = useSelector(selectSearchFilteredItems);
  const lanesTiers = useSelector(selectLanesTiers);
  const status = useSelector(selectStatus);
  const homeState = useSelector(selectHome);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const rsdMomentTypes = useSelector(
    (s: RootState) => s.rsdForms?.momentTypes ?? []
  );
  const highlightedIssue = useSelector(selectHighlightedIssue); // ✅ Add this to track single-click highlights
  const highlightSource = useSelector(selectHighlightSource); // ✅ Track source of highlights
  const mustWinViewActive = useSelector(selectMustWinView); // ✅ Track Must Wins view state (updates immediately on toggle)
  const displayedMustWinView = useDeferredValue(mustWinViewActive); // Deferred so view switch doesn't block; show Refreshing until this catches up

  // Sync transitioning state to Redux so header can show "Refreshing..." while deferred view catches up
  useEffect(() => {
    const transitioning = mustWinViewActive !== displayedMustWinView;
    dispatch(actions.setMustWinViewTransitioning(transitioning));
    return () => {
      dispatch(actions.setMustWinViewTransitioning(false));
    };
  }, [mustWinViewActive, displayedMustWinView, dispatch]);

  // Logic to determine if no cards are available
  // Ignore Key Dates and Events, only check for main releases: Price Changes and Offers, L1, L2, L3, All Other Releases
  const hasNoData = useMemo(() => {
    if (loading) return false; // Don't show message while loading

    // Don't show alert until initial load is complete
    if (!initialLoadComplete.current) {
      return false;
    }

    // Check if releases data has been loaded
    if (!releases?.releaseScheduled || releases.releaseScheduled.length === 0) {
      return false; // Don't show alert if data hasn't loaded yet
    }

    // Don't show toast if search filter is active
    if (searchFilterActive) {
      return false;
    }

    // Check if user has actively applied any filters (excluding search)
    const hasActiveFilters =
      categories.selectedCats.length > 0 ||
      productGroups.selectedGroups.length > 0 ||
      audiences.selectedAudiences.length > 0 ||
      selectedEvents.selectedEvents.length > 0 ||
      selectedFeatures.selectedFeatures.length > 0 ||
      selectedProducts.selectedProducts.length > 0 ||
      genAiFilter.selectedFilters.length > 0 ||
      lanesTiers.selectedLanesTiers.length > 0 ||
      momentTypeFilter.selectedMomentTypes.length > 0;

    // Only show alert if filters are active
    if (!hasActiveFilters) {
      return false;
    }

    return !hasMainReleases(filteredReleases);
  }, [
    loading,
    filteredReleases,
    releases,
    categories.selectedCats,
    productGroups.selectedGroups,
    audiences.selectedAudiences,
    selectedEvents.selectedEvents,
    selectedFeatures.selectedFeatures,
    selectedProducts.selectedProducts,
    genAiFilter.selectedFilters,
    lanesTiers.selectedLanesTiers,
    momentTypeFilter.selectedMomentTypes,
    searchFilterActive
  ]);

  // "My Stuff" has no active-filter requirement (unlike hasNoData's toast, which only
  // fires when the user has applied filters) — a brand-new, filter-free My Stuff view
  // with zero Primary Contact cards would otherwise render a silently empty grid.
  const myStuffHasNoCards = useMemo(() => {
    if (!homeState.isMyStuffViewActive) return false;
    if (loading) return false;
    if (!initialLoadComplete.current) return false;
    if (!releases?.releaseScheduled || releases.releaseScheduled.length === 0) {
      return false;
    }
    return !hasMainReleases(filteredReleases);
  }, [homeState.isMyStuffViewActive, loading, filteredReleases, releases]);

  // Check if surface (features) filter is active
  const surfaceFilterActive = selectedFeatures.selectedFeatures.length > 0;

  // Check if Gen AI filter is active
  const genAiFilterActive = genAiFilter.selectedFilters.length > 0;

  // Check if Revenue Impact filter is active
  const revenueImpactFilterActive =
    revenueImpactFilter.selectedFilters.length > 0;

  // Check if events filter is active
  const eventsFilterActive = selectedEvents.selectedEvents.length > 0;

  // Generate filter options dynamically from releaseScheduled data
  const filterOptions = useMemo(() => {
    const options = [{ key: 'all', label: 'All' }];

    if (releases?.releaseScheduled) {
      const types = releases.releaseScheduled.map(
        (item: any) => item.type as string
      );
      const uniqueTypes: string[] = Array.from(new Set(types));

      uniqueTypes.forEach(type => {
        options.push({
          key: type.toLowerCase().replace(/\s+/g, '-'),
          label: type
        });
      });
    }

    return options;
  }, [releases?.releaseScheduled]);

  // Memoized list of releases to render in Roadmap timeline (avoids inline filter on every render)
  const releasesToRender = useMemo(() => {
    if (!filteredReleases || filteredReleases.length === 0) return [];
    if (!hideEmptySwimLanes) return filteredReleases;
    return filteredReleases.filter(release => {
      return (
        release.projects &&
        release.projects.length > 0 &&
        release.projects.some(
          project => project.project && project.project.length > 0
        )
      );
    });
  }, [filteredReleases, hideEmptySwimLanes]);

  // Function to apply search filtering to releases (memoized for performance)
  const applySearchFilter = useCallback(
    (releaseData: any) => {
      if (!searchFilterActive || searchFilteredItems.length === 0) {
        return releaseData;
      }

      // Helper function to check if an item matches search filter (handles composite keys)
      const matchesSearchFilter = (
        id: string,
        releasePhaseId?: string,
        showOnTimeline?: boolean
      ) => {
        // Check simple ID match
        if (searchFilteredItems.includes(id)) {
          return true;
        }
        // Check composite key match - try both formats
        if (releasePhaseId !== undefined) {
          // Try phase-only key first (matches both timeline and roadmap cards)
          const phaseOnlyKey = `${id}-${releasePhaseId}`;
          if (searchFilteredItems.includes(phaseOnlyKey)) {
            return true;
          }

          // Also try with showOnTimeline for backward compatibility
          if (showOnTimeline !== undefined) {
            const fullCompositeKey = `${id}-${releasePhaseId}-${showOnTimeline}`;
            if (searchFilteredItems.includes(fullCompositeKey)) {
              return true;
            }
          }
        }
        return false;
      };

      const filteredReleaseScheduled = releaseData.releaseScheduled
        ?.map((releaseType: any) => {
          const filteredProjects = releaseType.projects
            ?.map((quarterProject: any) => {
              const filteredQuarterProjects = quarterProject.project
                ?.filter((project: any) => {
                  // Check if the project itself matches (with phase info)
                  const projectMatches = matchesSearchFilter(
                    project.issueId,
                    project.releasePhaseId,
                    project.showOnTimeline
                  );

                  // Check if any direct child projects match
                  const hasMatchingDirectChildren =
                    project.childRelationships?.some((child: any) =>
                      matchesSearchFilter(
                        child.issueId,
                        child.releasePhaseId,
                        child.showOnTimeline
                      )
                    );

                  // Check if any nested child projects match (grandchildren)
                  const hasMatchingNestedChildren =
                    project.childRelationships?.some((child: any) =>
                      child.childRelationships?.some((grandChild: any) =>
                        matchesSearchFilter(
                          grandChild.issueId,
                          grandChild.releasePhaseId,
                          grandChild.showOnTimeline
                        )
                      )
                    );

                  // Check if any features match (check both featureId and issueId)
                  const hasMatchingFeatures = project.features?.some(
                    (feature: any) => {
                      const featureUniqueId =
                        feature.featureId || feature.issueId;
                      return matchesSearchFilter(featureUniqueId);
                    }
                  );

                  return (
                    projectMatches ||
                    hasMatchingDirectChildren ||
                    hasMatchingNestedChildren ||
                    hasMatchingFeatures
                  );
                })
                .map((project: any) => {
                  // Check if the project itself matches
                  const projectMatches = matchesSearchFilter(
                    project.issueId,
                    project.releasePhaseId,
                    project.showOnTimeline
                  );

                  // If ONLY the parent matches (not children or features), return as-is
                  const hasMatchingChildren = project.childRelationships?.some(
                    (child: any) =>
                      matchesSearchFilter(
                        child.issueId,
                        child.releasePhaseId,
                        child.showOnTimeline
                      ) ||
                      child.childRelationships?.some((grandChild: any) =>
                        matchesSearchFilter(
                          grandChild.issueId,
                          grandChild.releasePhaseId,
                          grandChild.showOnTimeline
                        )
                      )
                  );

                  // Check if we need to filter features (check both featureId and issueId)
                  const hasMatchingFeatures = project.features?.some(
                    (feature: any) => {
                      const featureUniqueId =
                        feature.featureId || feature.issueId;
                      return matchesSearchFilter(featureUniqueId);
                    }
                  );

                  // If project itself matches but no children or features match, return as-is
                  if (
                    projectMatches &&
                    !hasMatchingChildren &&
                    !hasMatchingFeatures
                  ) {
                    return project;
                  }

                  // Prepare filtered children if needed
                  let filteredChildren = project.childRelationships;
                  if (hasMatchingChildren) {
                    // Filter and process children
                    filteredChildren = project.childRelationships
                      .map((child: any) => {
                        // Check if this child itself matches (with phase info)
                        if (
                          matchesSearchFilter(
                            child.issueId,
                            child.releasePhaseId,
                            child.showOnTimeline
                          )
                        ) {
                          return child;
                        }

                        // Check if any of its nested children match
                        const hasMatchingGrandChildren =
                          child.childRelationships?.some((grandChild: any) =>
                            matchesSearchFilter(
                              grandChild.issueId,
                              grandChild.releasePhaseId,
                              grandChild.showOnTimeline
                            )
                          );

                        if (hasMatchingGrandChildren) {
                          // Return child with filtered grandchildren
                          return {
                            ...child,
                            childRelationships: child.childRelationships.filter(
                              (grandChild: any) =>
                                matchesSearchFilter(
                                  grandChild.issueId,
                                  grandChild.releasePhaseId,
                                  grandChild.showOnTimeline
                                )
                            )
                          };
                        }

                        return null;
                      })
                      .filter(child => child !== null);
                  }

                  // Prepare filtered features if needed
                  let filteredFeatures = project.features;
                  if (hasMatchingFeatures) {
                    // Filter and process features (check both featureId and issueId)
                    filteredFeatures = project.features.filter(
                      (feature: any) => {
                        const featureUniqueId =
                          feature.featureId || feature.issueId;
                        return searchFilteredItems.includes(featureUniqueId);
                      }
                    );
                  }

                  // ✅ FIX: Filter features when GenAI and/or Surface filters are active
                  if (surfaceFilterActive || genAiFilterActive) {
                    filteredFeatures = (
                      filteredFeatures ||
                      project.features ||
                      []
                    ).filter((feature: any) => {
                      // Check surface match
                      let matchesSurface = !surfaceFilterActive; // Pass if filter not active
                      if (
                        surfaceFilterActive &&
                        Array.isArray(feature.surfaces)
                      ) {
                        matchesSurface = feature.surfaces.some(surface =>
                          selectedFeatures.selectedFeatures.includes(surface)
                        );
                      }

                      // Check GenAI match
                      let matchesGenAi = !genAiFilterActive; // Pass if filter not active
                      if (genAiFilterActive) {
                        if (
                          genAiFilter.selectedFilters.includes(
                            'gen-ai-all-features'
                          )
                        ) {
                          matchesGenAi = feature.genai === true;
                        } else if (
                          genAiFilter.selectedFilters.includes(
                            'gen-ai-consumes-credits'
                          )
                        ) {
                          matchesGenAi =
                            feature.genai === true &&
                            feature.consumesCredit === true;
                        }
                      }

                      // ✅ Use AND logic: feature must match ALL active filters
                      return matchesSurface && matchesGenAi;
                    });
                  }

                  // Return project with filtered children and/or features
                  return {
                    ...project,
                    ...(hasMatchingChildren && {
                      childRelationships: filteredChildren
                    }),
                    ...(hasMatchingFeatures && { features: filteredFeatures })
                  };
                });

              return {
                ...quarterProject,
                project: filteredQuarterProjects || []
              };
            })
            .filter((quarterProject: any) => quarterProject.project.length > 0);

          return {
            ...releaseType,
            projects: filteredProjects || []
          };
        })
        .filter((releaseType: any) => releaseType.projects.length > 0);

      const result = {
        ...releaseData,
        releaseScheduled: filteredReleaseScheduled || []
      };

      return result;
    },
    [searchFilterActive, searchFilteredItems]
  );

  // Handle filter selection changes

  // Initialize Key Dates cleanup on mount
  useEffect(() => {
    // Clean up old showKeyDates property from localStorage
    cleanupOldShowKeyDatesPreference();
  }, []);

  // Initialize filter selection from localStorage or set defaults
  useEffect(() => {
    if (filterOptions.length > 0) {
      const storedFilters = localStorage.getItem('roadmap_filter_selections');

      if (storedFilters) {
        try {
          const parsedFilters = JSON.parse(storedFilters);
          if (Array.isArray(parsedFilters)) {
            // Check if "All" was previously selected
            const wasAllSelected = parsedFilters.includes('all');

            if (wasAllSelected) {
              // If "All" was selected, include all current options
              const allCurrentOptions = new Set(
                filterOptions.map(option => option.key)
              );
              setSelectedFilterOptions(allCurrentOptions);
              // Update localStorage with all current options
              localStorage.setItem(
                'roadmap_filter_selections',
                JSON.stringify(Array.from(allCurrentOptions))
              );
              return;
            }

            // Validate that stored filters still exist in current options
            const validFilters = parsedFilters.filter(filter =>
              filterOptions.some(option => option.key === filter)
            );

            if (validFilters.length > 0) {
              setSelectedFilterOptions(new Set(validFilters));
              return;
            }
          }
        } catch (error) {
          console.error('Error parsing stored filter selections:', error);
        }
      }

      // If no valid stored filters, use default selection (exclude "Key Dates" and "Other Releases" by default)
      if (
        selectedFilterOptions instanceof Set &&
        selectedFilterOptions.size === 0
      ) {
        const defaultSelection = new Set(
          filterOptions
            .filter(option => {
              if (option.key === 'all') return false;
              if (option.key === 'all-other-releases') return false;
              if (option.key === 'key-dates') return false; // Key Dates not selected by default
              return true;
            })
            .map(option => option.key)
        );
        setSelectedFilterOptions(defaultSelection);
      }
    }
  }, [filterOptions]);

  // Cleanup tooltip and toast timers on unmount
  React.useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      if (noDataAlertTimerRef.current) {
        clearTimeout(noDataAlertTimerRef.current);
      }
    };
  }, []);

  // Show toast when no data is found (with debounce to prevent false positives during filter updates)
  // Only show toast when slideout is closed - user shouldn't see background toast messages
  useEffect(() => {
    // Clear any existing timer
    if (noDataAlertTimerRef.current) {
      clearTimeout(noDataAlertTimerRef.current);
    }

    // Only show toast if slideout is closed
    if (hasNoData && !isSlideoutOpen) {
      // Debounce the toast by 300ms to allow data to update after filter changes
      noDataAlertTimerRef.current = setTimeout(() => {
        ToastQueue.info(
          'No results found for the selected filters. Try adjusting your filter criteria.',
          { id: 'no-data-alert', timeout: 5000 }
        );
      }, 300);
    }

    // Cleanup on unmount or when hasNoData changes
    return () => {
      if (noDataAlertTimerRef.current) {
        clearTimeout(noDataAlertTimerRef.current);
      }
    };
  }, [hasNoData, isSlideoutOpen]);

  // Add this new function to determine which quarter should be expanded by default
  const getDefaultExpandedQuarter = (quarters: Quarter[]) => {
    const today = new Date();
    const currentMonth = today.toLocaleString('default', { month: 'short' });
    const currentYear = today.getFullYear();

    for (let i = 0; i < quarters.length; i++) {
      const quarter = quarters[i];
      // Extract year from label, e.g., "Q2 2025" => 2025
      const quarterYear = parseInt(quarter.label.split(' ')[1], 10);

      if (
        quarterYear === currentYear &&
        quarter.months.includes(currentMonth)
      ) {
        // If it's the last month of the quarter
        if (quarter.months[2] === currentMonth) {
          // Only expand the next quarter if it exists in the range
          if (i < quarters.length - 1) {
            return quarters[i + 1].label;
          } else {
            // Last quarter in range, do not expand anything
            return '';
          }
        } else {
          // Otherwise expand current quarter
          return quarter.label;
        }
      }
    }
    // If current month/year is not in the selected range, return empty string
    return '';
  };

  useEffect(() => {
    // Default to current date
    const defaultQuarters = generateQuarters(dateRange.start, dateRange.end);
    setQuarters(defaultQuarters);

    // Set the default expanded quarter
    const defaultExpandedQuarter = getDefaultExpandedQuarter(defaultQuarters);
    if (defaultExpandedQuarter) {
      setExpandedQuarters([defaultExpandedQuarter]);
    }
  }, [dateRange]);

  // Initialize state from localStorage on mount and when date range changes
  useEffect(() => {
    const loadPreferencesAndSetState = () => {
      // ✅ Clear manually expanded cards on page load/refresh
      sessionStorage.removeItem('manually_expanded_cards');

      const storedPreferences = localStorage.getItem(
        'roadmap_user_preferences'
      );
      const currentQuarters = generateQuarters(dateRange.start, dateRange.end);

      const urlHasViewState = shouldPreferUrlOverLocalPreferences();

      if (storedPreferences) {
        try {
          const preferences = JSON.parse(storedPreferences);

          // NOTE: isCompact/isUnNested are intentionally NOT re-dispatched from
          // localStorage here. Redux (state.home.isCompact/unNestAll) is the
          // single source of truth for these now — set by the toggle itself,
          // the one-time mount restore in ViewControls, or the Saved View
          // apply/stash-restore mechanism. Re-asserting a raw localStorage
          // snapshot on every dateRange change (this effect's trigger) used to
          // silently overwrite whatever a Saved View or the shared-preferences
          // restore had just correctly set, moments earlier.

          // Use compact from URL when deep link is active; otherwise the live
          // Redux value decides which quarters should show expanded by default.
          const urlCompact = urlHasViewState
            ? parseViewStateFromSearchParams(window.location.search).isCompact
            : undefined;
          const storedCompactMode = urlHasViewState
            ? urlCompact ?? isCompactRef.current
            : isCompactRef.current;

          // Load expanded quarters only if not in compact mode
          if (!storedCompactMode) {
            if (
              preferences.expandedQuarters &&
              Array.isArray(preferences.expandedQuarters) &&
              preferences.expandedQuarters.length > 0
            ) {
              // Validate that stored quarters still exist in current date range
              const validStoredQuarters = preferences.expandedQuarters.filter(
                quarter => currentQuarters.some(q => q.label === quarter)
              );
              if (validStoredQuarters.length > 0) {
                setExpandedQuarters(validStoredQuarters);
              } else {
                // If no valid stored quarters, use default quarter logic
                const defaultExpandedQuarter =
                  getDefaultExpandedQuarter(currentQuarters);
                setExpandedQuarters(
                  defaultExpandedQuarter ? [defaultExpandedQuarter] : []
                );
              }
            } else {
              // If expandedQuarters is empty or doesn't exist, use default quarter logic
              const defaultExpandedQuarter =
                getDefaultExpandedQuarter(currentQuarters);
              setExpandedQuarters(
                defaultExpandedQuarter ? [defaultExpandedQuarter] : []
              );
            }
          } else {
            // If compact mode is enabled, collapse all quarters
            setExpandedQuarters([]);
          }
        } catch (error) {
          console.error('Error parsing stored preferences:', error);
          // If there's an error parsing, use default quarter logic
          const defaultExpandedQuarter =
            getDefaultExpandedQuarter(currentQuarters);
          setExpandedQuarters(
            defaultExpandedQuarter ? [defaultExpandedQuarter] : []
          );
        }
      } else {
        // If no stored preferences, use default quarter logic
        const defaultExpandedQuarter =
          getDefaultExpandedQuarter(currentQuarters);
        setExpandedQuarters(
          defaultExpandedQuarter ? [defaultExpandedQuarter] : []
        );
      }
    };

    loadPreferencesAndSetState();
  }, [dateRange, dispatch, actions]);

  // Handle compact mode only. Do NOT collapse quarters when entering presentation (fullscreen) mode:
  // presentation mode should show the existing view as-is with no state changes; when the user exits
  // fullscreen, expanded quarters must be unchanged.
  useEffect(() => {
    if (isCompact) {
      setExpandedQuarters([]);
    }
    // Note: Quarter restoration when exiting compact mode is now handled specifically
    // in the ViewControls component's onCompactToggle function to avoid interference
    // from other components that programmatically set compact mode to false
  }, [isCompact]);

  // CRITICAL: Ensure cards are nested when no filters requiring unnesting are active
  // This prevents cards from staying unnested when all filters are cleared
  useEffect(() => {
    const surfacesActive = selectedFeatures.selectedFeatures.length > 0;
    const genAiActive = genAiFilter.selectedFilters.length > 0;
    const searchActive = searchFilterActive;

    // ✅ If no filters that require unnesting are active AND cards are currently unnested
    // Then check if we should automatically nest them
    // BUT: Do NOT auto-nest if user has explicitly clicked global unnest button
    if (!surfacesActive && !genAiActive && !searchActive && isUnNested) {
      // Shared view: URL owns unnest state — do not auto-nest or read localStorage.
      if (shouldSkipPreferencePersistence()) {
        return;
      }

      let hasExplicitUnnestPreference = false;

      const storedPreferences = localStorage.getItem(
        'roadmap_user_preferences'
      );

      if (storedPreferences) {
        try {
          const preferences = JSON.parse(storedPreferences);
          // If user has explicitly saved isUnNested: true, respect it
          hasExplicitUnnestPreference = preferences.isUnNested === true;
        } catch (error) {
          // If parsing fails, no user preference
        }
      }

      // ✅ Only auto-nest if user did NOT explicitly click global unnest
      // This preserves global unnest state when filters are cleared
      if (!hasExplicitUnnestPreference) {
        dispatch(actions.setUnNestAll(false));

        // ⚠️ IMPORTANT: DO NOT clear highlights if events are selected OR if single-click highlights exist!
        const hasActiveEventFilter = selectedEvents?.selectedEvents?.length > 0;
        const hasSingleClickHighlights =
          highlightedIssue && Object.keys(highlightedIssue).length > 0;
        const highlightSourceIsSingleClick = highlightSource === 'single-click';

        if (!hasActiveEventFilter && !highlightSourceIsSingleClick) {
          dispatch(
            actions.updateHighlightedIssue({
              relatedIssues: [],
              unnestParentIds: []
            })
          );
        }
      }
    }
  }, [
    selectedFeatures.selectedFeatures,
    genAiFilter.selectedFilters,
    searchFilterActive,
    isUnNested,
    dispatch,
    actions,
    selectedEvents,
    highlightedIssue,
    highlightSource
  ]);

  // Listen for quarter restoration events from ViewControls
  useEffect(() => {
    const handleRestoreQuarters = (event: CustomEvent) => {
      const { expandedQuarters: quartersToRestore } = event.detail;
      const currentQuarters = generateQuarters(dateRange.start, dateRange.end);

      // Validate that stored quarters still exist in current date range
      const validStoredQuarters = quartersToRestore.filter(quarter =>
        currentQuarters.some(q => q.label === quarter)
      );

      if (validStoredQuarters.length > 0) {
        setExpandedQuarters(validStoredQuarters);
      } else {
        // If no valid stored quarters, use default quarter logic
        const defaultExpandedQuarter =
          getDefaultExpandedQuarter(currentQuarters);
        setExpandedQuarters(
          defaultExpandedQuarter ? [defaultExpandedQuarter] : []
        );
      }
    };

    window.addEventListener(
      'restoreQuarters',
      handleRestoreQuarters as EventListener
    );
    return () => {
      window.removeEventListener(
        'restoreQuarters',
        handleRestoreQuarters as EventListener
      );
    };
  }, [dateRange]);

  // Listen for card size restoration events from ViewControls
  useEffect(() => {
    const handleRestoreCardSize = (event: CustomEvent) => {
      const { collapseAll: savedCollapseAll } = event.detail;

      // Only restore if the current Redux state doesn't match the saved preference
      if (savedCollapseAll !== isCollapsed) {
        dispatch(actions.setCollapseAll()); // This toggles the current state
      }
    };

    window.addEventListener(
      'restoreCardSize',
      handleRestoreCardSize as EventListener
    );
    return () => {
      window.removeEventListener(
        'restoreCardSize',
        handleRestoreCardSize as EventListener
      );
    };
  }, [isCollapsed]);

  // Listen for unnest state restoration events from ViewControls
  useEffect(() => {
    const handleRestoreUnNestState = (event: CustomEvent) => {
      const { isUnNested: savedIsUnNested } = event.detail;

      // Only restore if the current Redux state doesn't match the saved preference
      if (savedIsUnNested !== isUnNested) {
        dispatch(actions.setUnNestAll(savedIsUnNested)); // Set to the saved state
      }
    };

    window.addEventListener(
      'restoreUnNestState',
      handleRestoreUnNestState as EventListener
    );
    return () => {
      window.removeEventListener(
        'restoreUnNestState',
        handleRestoreUnNestState as EventListener
      );
    };
  }, [isUnNested]);

  // Calculate and log the gap between roadmap-row quarter and browser window
  useEffect(() => {
    const calculateGap = () => {
      // Find all roadmap-row-quarter elements
      const quarterElements = document.querySelectorAll(
        '[data-testid^="roadmap-row-quarter-"]'
      );

      if (quarterElements.length > 0) {
        // Get the last (rightmost) quarter element
        const lastQuarter = quarterElements[
          quarterElements.length - 1
        ] as HTMLElement;
        const rect = lastQuarter.getBoundingClientRect();

        // Calculate the gap from the right edge to the browser window
        const browserWidth = window.innerWidth;
        const quarterRightBoundary = rect.right;
        const gapFromBrowserEdge = browserWidth - quarterRightBoundary;

        // Set the gap width to state
        setGapWidth(Math.max(gapFromBrowserEdge, 0));
      }
    };

    // Calculate after a delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateGap, 500);

    // Recalculate on window resize
    window.addEventListener('resize', calculateGap);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateGap);
    };
  }, [quarters, expandedQuarters, isCompact, filteredReleases]);

  // Define the fixed order for lanes/tiers
  const getLaneOrder = (type: string): number => {
    const order: Record<string, number> = {
      Events: 0,
      'Price Changes and Offers': 1,
      L1: 2,
      L2: 3,
      L3: 4,
      'All Other Releases': 5
    };
    return order[type] ?? 999; // Put unknown types at the end
  };

  // Sort releases by the defined order
  const sortReleasesByOrder = (releases: any[]): any[] => {
    return [...releases].sort((a, b) => {
      const orderA = getLaneOrder(a.type);
      const orderB = getLaneOrder(b.type);
      return orderA - orderB;
    });
  };

  const exitCompactIfNeeded = useCallback(() => {
    if (!isCompact) {
      return;
    }

    dispatch(actions.setCompact(false));
    exitCompactModeAndRestoreTimeline('quarterly');
  }, [actions, dispatch, isCompact]);

  const onCollapseAll = () => {
    // This function handles the "Collapse All" button - it should only toggle card size (big/small)
    // It should NOT affect quarter expansion state
    dispatch(actions.setCollapseAll());

    // Save the card size preference to localStorage
    const preferences = getUserPreferences();
    const newCollapseAllState = !isCollapsed; // Will be the new state after toggle
    saveUserPreferences({
      ...preferences,
      collapseAll: newCollapseAllState, // Save the toggled card size preference
      isUnNested: false // Reset un-nest state when toggling card size
    });

    // Do not collapse quarters - only toggle card size
    // The quarter expansion state should remain unchanged
  };
  const onExpandQuarter = quarter => {
    // If compact mode is enabled, first disable compact mode and expand the clicked quarter
    if (isCompact) {
      dispatch(actions.setCompact(false));

      // Expand the clicked quarter
      const newExpandedQuarters = [quarter];
      setExpandedQuarters(newExpandedQuarters);

      // Update both compact mode and expandedQuarters in preferences using helper
      saveUserPreferences({
        isCompact: false,
        expandedQuarters: newExpandedQuarters
      });
      return;
    }

    // Update state first
    const newExpandedQuarters = expandedQuarters.includes(quarter)
      ? expandedQuarters.filter(q => q !== quarter) // Remove if already expanded
      : [...expandedQuarters, quarter]; // Add to expanded quarters array

    setExpandedQuarters(newExpandedQuarters);

    // Update expandedQuarters in preferences using helper
    saveUserPreferences({ expandedQuarters: newExpandedQuarters });
  };

  function generateQuarters(
    startDate: string = moment().format('YYYY-MM-DD'),
    endDate: string = moment().add(6, 'months').format('YYYY-MM-DD')
  ): Quarter[] {
    const quarterMapping: Record<number, string[]> = {
      1: ['Dec', 'Jan', 'Feb'],
      2: ['Mar', 'Apr', 'May'],
      3: ['Jun', 'Jul', 'Aug'],
      4: ['Sep', 'Oct', 'Nov']
    };

    const getQuarterAndYear = (
      date: moment.Moment
    ): { quarter: number; year: number } => {
      const month = date.month(); // Month is zero-based
      const year = date.year();
      if (month === 11) return { quarter: 1, year: year + 1 }; // December belongs to Q1 of next year
      if (month <= 1) return { quarter: 1, year }; // Jan, Feb
      if (month <= 4) return { quarter: 2, year }; // Mar, Apr, May
      if (month <= 7) return { quarter: 3, year }; // Jun, Jul, Aug
      return { quarter: 4, year }; // Sep, Oct, Nov
    };

    // Parse start and end dates
    const start = moment(startDate);
    const end = moment(endDate);

    // Get the full quarters for the start and end dates
    const { quarter: startQuarter, year: startYear } = getQuarterAndYear(start);
    const { quarter: endQuarter, year: endYear } = getQuarterAndYear(end);

    const quarters: Quarter[] = [];
    let currentQuarter = startQuarter;
    let currentYear = startYear;
    let quarterId = 1;

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentQuarter <= endQuarter)
    ) {
      const label = `Q${currentQuarter} ${currentYear}`;
      const displayLabel = `Q${currentQuarter}'${currentYear
        .toString()
        .slice(-2)}`;
      const monthsForQuarter = quarterMapping[currentQuarter];

      quarters.push({
        id: quarterId,
        label,
        displayLabel,
        months: monthsForQuarter
      });

      quarterId++;
      currentQuarter++;
      if (currentQuarter > 4) {
        currentQuarter = 1;
        currentYear++;
      }
    }
    return quarters;
  }

  useEffect(() => {
    if (!releases?.releaseScheduled || releases.releaseScheduled.length === 0) {
      dispatch(actions.setFilteredReleases([]));
      setKeyDatesRelease(null);
      return;
    }

    // Performance: Use requestIdleCallback for non-critical filtering
    const timeStart = performance.now();

    // Get selected filter types from Redux state
    const selectedTypes = lanesTiers.selectedLanesTiers || [];
    // Check if "All" is selected or if all individual options are selected
    const isAllSelected =
      selectedTypes.includes('all') ||
      (selectedTypes.length === filterOptions.length - 1 &&
        !selectedTypes.includes('all'));

    // Apply filter dropdown logic to determine which releases to show
    const shouldShowRelease = (releaseType: string) => {
      // ⭐ HIGHEST PRIORITY: If events are selected, show ALL release types (event filter supersedes everything)
      if (selectedEvents.selectedEvents.length > 0) {
        return true;
      }

      // If search is active, override filter dropdown and show all release types
      if (searchFilterActive && searchFilteredItems.length > 0) {
        return true;
      }

      if (isAllSelected) return true;

      const releaseKey = releaseType.toLowerCase().replace(/\s+/g, '-');
      return selectedTypes.includes(releaseKey);
    };

    // ⭐ HIGHEST PRIORITY: Apply event filtering FIRST on full unfiltered data (supersedes search and other filters)
    // Apply search filtering only if events are NOT selected
    const searchFilteredReleases =
      selectedEvents.selectedEvents.length > 0
        ? releases // Use full data when events are selected
        : searchFilterActive && searchFilteredItems.length > 0
        ? applySearchFilter(releases)
        : releases;

    // If search is active AND events are NOT selected, highlight matching items and unnest their parents
    if (
      searchFilterActive &&
      searchFilteredItems.length > 0 &&
      selectedEvents.selectedEvents.length === 0
    ) {
      const matchedItems: Set<string> = new Set();
      const parentToUnnest: Set<string> = new Set();

      // Scan all releases to find matching items and their parents
      searchFilteredReleases.releaseScheduled.forEach(release => {
        release.projects.forEach(project => {
          project.project.forEach(subProject => {
            // Check if the project itself matches
            if (searchFilteredItems.includes(subProject.issueId)) {
              matchedItems.add(subProject.issueId);
              parentToUnnest.add(release.issueId);
            }

            // Check if any child relationships match
            if (subProject.childRelationships) {
              subProject.childRelationships.forEach((child: any) => {
                if (searchFilteredItems.includes(child.issueId)) {
                  matchedItems.add(child.issueId);
                  parentToUnnest.add(subProject.issueId);
                  parentToUnnest.add(release.issueId);
                }

                // Check nested children (grandchildren)
                if (child.childRelationships) {
                  child.childRelationships.forEach((grandChild: any) => {
                    if (searchFilteredItems.includes(grandChild.issueId)) {
                      matchedItems.add(grandChild.issueId);
                      parentToUnnest.add(child.issueId);
                      parentToUnnest.add(subProject.issueId);
                      parentToUnnest.add(release.issueId);
                    }
                  });
                }
              });
            }

            // Check if any features match (use featureId or issueId)
            if (subProject.features) {
              subProject.features.forEach((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                if (searchFilteredItems.includes(featureUniqueId)) {
                  matchedItems.add(featureUniqueId);
                  parentToUnnest.add(subProject.issueId);
                  parentToUnnest.add(release.issueId);
                }
              });
            }
          });
        });

        // Also check for features at release level (Product Releases with/without Marketing)
        if (Array.isArray(release.features) && release.features.length > 0) {
          release.features.forEach(feature => {
            const featureUniqueId = feature.featureId || feature.issueId;
            if (searchFilteredItems.includes(featureUniqueId)) {
              matchedItems.add(featureUniqueId);
              parentToUnnest.add(release.issueId);
            }
          });
        }
      });

      // Dispatch actions to highlight matched items and unnest their parents
      if (matchedItems.size > 0) {
        // Combine matched items and parent IDs for highlighting
        const allIdsToHighlight = [
          ...Array.from(matchedItems),
          ...Array.from(parentToUnnest)
        ];

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentToUnnest)
          })
        );
        dispatch(actions.savePreviousUnNestAllState());
      }
    }

    // Find the actual Key Dates and Events releases first
    const keyDatesRelease = searchFilteredReleases.releaseScheduled.find(
      release => release.type === 'Key Dates'
    );
    const eventsRelease = searchFilteredReleases.releaseScheduled.find(
      release => release.type.includes('Events')
    );

    // Extract Key Dates separately (only if Key Dates is selected in filter)
    // ⭐ IMPORTANT: Key Dates should NEVER be affected by any filters (GenAI, Surfaces, etc.)
    // They are standalone calendar items, not feature-based
    const keyDates =
      keyDatesRelease && shouldShowRelease(keyDatesRelease.type)
        ? keyDatesRelease
        : null;

    // Extract Events separately (only if Events is selected in filter)
    // ⭐ IMPORTANT: Events should NEVER be affected by filters (GenAI, Surfaces, etc.)
    // However, when event filter is active, we DO apply other filters to the RELATED implementations
    // CRITICAL FIX: If user has selected specific events in the events filter,
    // show the Events lane even if it's hidden in the lanes/tiers filter
    const events =
      eventsRelease &&
      (shouldShowRelease(eventsRelease.type) || eventsFilterActive)
        ? eventsRelease
        : null;

    // Keep track of all child issue IDs to filter them out from top-level display
    const childIssueIds = new Set<string>();
    releases.releaseScheduled.forEach(release => {
      release.projects.forEach(project => {
        project.project.forEach(subProject => {
          if (subProject.childRelationships) {
            subProject.childRelationships.forEach(child => {
              childIssueIds.add(child.issueId);
            });
          }
        });
      });
    });

    // Filter other releases based on filter dropdown selection
    const filteredReleasesData = searchFilteredReleases.releaseScheduled
      .filter(release => {
        // Skip Key Dates (handled separately)
        if (release.type === 'Key Dates') return false;

        // Skip Events if not selected or if Events is handled separately
        if (release.type.includes('Events')) return false;

        // Apply filter dropdown logic only when no surface/Gen AI filters
        return shouldShowRelease(release.type);
      })
      .map(schedule => {
        const filteredProjects = schedule.projects
          .map(project => {
            // Convert searchFilteredItems to Set once per project for faster lookups
            const searchFilteredSet =
              searchFilterActive && searchFilteredItems.length > 0
                ? new Set(searchFilteredItems)
                : null;

            const filteredSubProjects = project.project.filter(subProject => {
              // ⭐ SEARCH SUPERSEDES ALL OTHER FILTERS
              // If search is active, ONLY check search matches and ignore all other filters
              if (searchFilterActive && searchFilteredSet) {
                // Helper function to check if an ID matches search filter (handles composite keys)
                const matchesSearchFilter = (
                  id: string,
                  releasePhaseId?: string,
                  showOnTimeline?: boolean
                ) => {
                  // Check simple ID match first
                  if (searchFilteredSet.has(id)) {
                    return true;
                  }

                  // Check composite key matches (both phase-only and full)
                  if (releasePhaseId !== undefined) {
                    // Check phase-only key first (matches both timeline and roadmap cards)
                    const phaseOnlyKey = `${id}-${releasePhaseId}`;
                    if (searchFilteredSet.has(phaseOnlyKey)) {
                      return true;
                    }

                    // Also check full composite key for backward compatibility
                    if (showOnTimeline !== undefined) {
                      const fullCompositeKey = `${id}-${releasePhaseId}-${showOnTimeline}`;
                      if (searchFilteredSet.has(fullCompositeKey)) {
                        return true;
                      }
                    }
                  }

                  return false;
                };

                // Check if the project itself matches (with phase info if available)
                if (
                  matchesSearchFilter(
                    subProject.issueId,
                    subProject.releasePhaseId,
                    subProject.showOnTimeline
                  )
                ) {
                  return true;
                }

                // Check if any of the project's children match
                if (
                  subProject.childRelationships?.some((child: any) =>
                    matchesSearchFilter(
                      child.issueId,
                      child.releasePhaseId,
                      child.showOnTimeline
                    )
                  )
                ) {
                  return true;
                }

                // Check if any of the project's features match (check both featureId and issueId)
                if (
                  subProject.features?.some((feature: any) => {
                    const featureUniqueId =
                      feature.featureId || feature.issueId;
                    return matchesSearchFilter(featureUniqueId);
                  })
                ) {
                  return true;
                }

                // If search is active but this item doesn't match, exclude it
                return false;
              }

              // ⭐ ONLY APPLY OTHER FILTERS WHEN SEARCH AND EVENTS ARE NOT ACTIVE
              // Skip if this is a child issue that should be nested (only when search/events are not active)
              if (childIssueIds.has(subProject.issueId)) {
                return false;
              }

              // ⭐ EVENTS SUPERSEDE ALL OTHER FILTERS (except search, revenue impact, and must win)
              // If events filter is active, bypass audience, product group, category, product filters
              // Events should work on the FULL dataset
              // BUT still apply revenue impact and must win filters even when events filter is active
              if (selectedEvents.selectedEvents.length > 0) {
                // Apply revenue impact filter even when events filter is active
                if (revenueImpactFilterActive) {
                  const hasYesFilter =
                    revenueImpactFilter.selectedFilters.includes(
                      'revenue-impact-yes'
                    );
                  const hasNoFilter =
                    revenueImpactFilter.selectedFilters.includes(
                      'revenue-impact-no'
                    );
                  const revenueImpactValue = subProject.revenueImpact === true;

                  // If both Yes and No are selected, continue to check must win
                  if (!(hasYesFilter && hasNoFilter)) {
                    // If only Yes is selected, show only cards with revenueImpact === true
                    if (
                      hasYesFilter &&
                      !hasNoFilter &&
                      revenueImpactValue !== true
                    ) {
                      return false;
                    }

                    // If only No is selected, show only cards with revenueImpact === false
                    if (
                      hasNoFilter &&
                      !hasYesFilter &&
                      revenueImpactValue !== false
                    ) {
                      return false;
                    }
                  }
                }

                return true; // Show all items when events filter is active (if revenue impact don't filter it out)
              }

              // In Must Wins view, audience filter is applied at swimlane level (which audience rows to show), not per-card
              const audienceMatch =
                displayedMustWinView ||
                audiences.selectedAudiences.length === 0 ||
                (Array.isArray(subProject.customerAudiences)
                  ? subProject.customerAudiences.some(aud =>
                      audiences.selectedAudiences.includes(aud)
                    )
                  : audiences.selectedAudiences.includes(
                      subProject.customerAudiences
                    ));
              const productGroupMatch =
                productGroups.selectedGroups.length === 0 ||
                (Array.isArray(subProject.productGroups)
                  ? subProject.productGroups.some(group =>
                      productGroups.selectedGroups.includes(group)
                    )
                  : productGroups.selectedGroups.includes(
                      subProject.productGroups
                    ));
              const categoryMatch =
                categories.selectedCats.length === 0 ||
                (Array.isArray(subProject.categories)
                  ? subProject.categories.some(cat =>
                      categories.selectedCats.includes(cat)
                    )
                  : categories.selectedCats.includes(subProject.categories));

              const productMatch =
                selectedProducts.selectedProducts.length === 0 ||
                (Array.isArray(subProject.products)
                  ? subProject.products.some(prod =>
                      selectedProducts.selectedProducts.includes(prod)
                    )
                  : selectedProducts.selectedProducts.includes(
                      subProject.products
                    ));

              // Status filter match - check if the project's riskLevel matches selected status filters
              const statusMatch =
                status.selectedStatus.length === 0 ||
                (() => {
                  const riskLevel = subProject.riskLevel;
                  const projectStatus = subProject.status;

                  return status.selectedStatus.some(selectedStatus => {
                    if (selectedStatus === 'all') return true;
                    if (
                      selectedStatus === 'risk-level-low' &&
                      riskLevel === RISK_LEVELS.LOW
                    )
                      return true;
                    if (
                      selectedStatus === 'risk-level-medium' &&
                      riskLevel === RISK_LEVELS.MODERATE
                    )
                      return true;
                    if (
                      selectedStatus === 'risk-level-high' &&
                      riskLevel === RISK_LEVELS.HIGH
                    )
                      return true;
                    if (
                      selectedStatus === 'in-planning' &&
                      riskLevel === RISK_LEVELS.IN_PLANNING
                    )
                      return true;
                    return false;
                  });
                })();

              // CSME-113550: Completed/Closed visibility is an independent AND-guard,
              // decoupled from the risk-level inclusive-OR statusMatch above. Shown by
              // default (hideCompletedItems === false); hidden only when the preference
              // is true AND the item's effective status (same showOnRoadmap/
              // showOnTimeline/secondaryDateStatus logic RoadmapCard/TimelineCard use
              // for the completed checkmark, not just the raw status field) is terminal.
              const normalizedEffectiveStatus = normalizeStatus(
                getEffectiveStatus(
                  subProject.status,
                  subProject.showOnRoadmap,
                  subProject.showOnTimeline,
                  subProject.secondaryDateStatus
                )
              );
              const isCompletedOrClosed =
                normalizedEffectiveStatus === STATUS_VALUES.COMPLETE ||
                normalizedEffectiveStatus === STATUS_VALUES.CLOSED;
              const hideCompletedMatch = !(
                homeState.hideCompletedItems && isCompletedOrClosed
              );

              // Check revenue impact filter - apply to all cards
              const revenueImpactMatch =
                !revenueImpactFilterActive ||
                (() => {
                  const hasYesFilter =
                    revenueImpactFilter.selectedFilters.includes(
                      'revenue-impact-yes'
                    );
                  const hasNoFilter =
                    revenueImpactFilter.selectedFilters.includes(
                      'revenue-impact-no'
                    );
                  const revenueImpactValue = subProject.revenueImpact === true;

                  // If both Yes and No are selected, show all (no filtering)
                  if (hasYesFilter && hasNoFilter) {
                    return true;
                  }

                  // If only Yes is selected, show only cards with revenueImpact === true
                  if (hasYesFilter && !hasNoFilter) {
                    return revenueImpactValue === true;
                  }

                  // If only No is selected, show only cards with revenueImpact === false
                  if (hasNoFilter && !hasYesFilter) {
                    return revenueImpactValue === false;
                  }

                  // If neither is selected (shouldn't happen if filter is active), show all
                  return true;
                })();

              const momentTypeMatch = subProjectMatchesMomentTypeSelection(
                subProject,
                momentTypeFilter.selectedMomentTypes,
                rsdMomentTypes
              );

              // ⭐ BASE FILTER: Only apply "permanent" filters (audiences, product groups, categories, products, status, revenue impact)
              // Surface and Gen AI are handled in dedicated sections later to allow proper parent/feature matching
              const is_select =
                audienceMatch &&
                productGroupMatch &&
                categoryMatch &&
                productMatch &&
                statusMatch &&
                hideCompletedMatch &&
                revenueImpactMatch &&
                momentTypeMatch;

              return is_select;
            });

            return filteredSubProjects.length > 0
              ? { ...project, project: filteredSubProjects }
              : null;
          })
          .filter(Boolean);

        // Also check if the schedule/release itself should be included:
        // 1. For surface filters: check if release has features with matching surfaces
        // 2. For search: check if release itself or its features are in searchFilteredItems

        // Check for search match at release level
        const scheduleMatchesSearch =
          searchFilterActive &&
          searchFilteredItems.length > 0 &&
          (searchFilteredItems.includes(schedule.issueId) ||
            (Array.isArray(schedule.features) &&
              schedule.features.some((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                return searchFilteredItems.includes(featureUniqueId);
              })));

        // Check for surface filter match at release level
        // This handles Product Releases with/without Marketing where features are at the release level
        const scheduleHasMatchingFeatures =
          selectedFeatures.selectedFeatures.length > 0 &&
          Array.isArray(schedule.features) &&
          schedule.features.length > 0 &&
          schedule.features.some(
            feature =>
              Array.isArray(feature.surfaces) &&
              feature.surfaces.some(surface =>
                selectedFeatures.selectedFeatures.includes(surface)
              )
          );

        // Check for Gen AI filter match at release level
        const scheduleHasMatchingGenAiFeatures =
          genAiFilter.selectedFilters.length > 0 &&
          Array.isArray(schedule.features) &&
          schedule.features.length > 0 &&
          schedule.features.some(feature => {
            if (genAiFilter.selectedFilters.includes('gen-ai-all-features')) {
              return feature.genai === true;
            } else if (
              genAiFilter.selectedFilters.includes('gen-ai-consumes-credits')
            ) {
              return feature.genai === true && feature.consumesCredit === true;
            }
            return false;
          });

        // Include the schedule if:
        // - It has filtered projects (which already have all filters applied at subProject level), OR
        // - It matches search criteria (when search is active), OR
        // - It has matching features at the release level (for surface and Gen AI filters)
        return filteredProjects.length > 0 ||
          scheduleMatchesSearch ||
          scheduleHasMatchingFeatures ||
          scheduleHasMatchingGenAiFeatures
          ? { ...schedule, projects: filteredProjects }
          : null;
      })
      .filter(Boolean);

    let processedEvents = events;
    let processedReleases = filteredReleasesData;

    // ⭐ Clear highlights when events filter becomes empty
    // This ensures highlights are removed when the last event is deselected
    // ⚠️ ONLY clear FILTER-based highlights, not single-click highlights
    if (selectedEvents.selectedEvents.length === 0) {
      // Check if other filters (surfaces, GenAI) are active
      const surfacesActive = selectedFeatures.selectedFeatures.length > 0;
      const genAiActive = genAiFilter.selectedFilters.length > 0;
      const hasSingleClickHighlights =
        highlightedIssue && Object.keys(highlightedIssue).length > 0;
      const highlightSourceIsSingleClick = highlightSource === 'single-click';
      const highlightSourceIsSearch = highlightSource === 'search'; // ✅ ALSO check for search highlights

      // ✅ ONLY clear filter-based highlights
      // ⚠️ NEVER clear single-click OR search highlights
      if (!highlightSourceIsSingleClick && !highlightSourceIsSearch) {
        // Always clear FILTER highlights when events are deselected
        // Other filters will re-apply their highlights if needed
        if (!surfacesActive && !genAiActive) {
          // No other filters active - clear all filter highlights

          dispatch(actions.clearHighlightedIssues());
        } else {
          // Other filters are active - clear highlights (they will be re-set by those filters)

          dispatch(actions.clearHighlightedIssues());
        }
      } else {
      }
    }

    // If events are selected, filter the events and their related implementations
    if (selectedEvents.selectedEvents.length > 0 && events) {
      // ⭐ EVENT FILTER HAS HIGHEST PRIORITY - work on full data, ignore search and other filters
      // Filter events to only show selected ones while maintaining the structure
      processedEvents = {
        ...events,
        projects: events.projects
          .map(quarterData => ({
            ...quarterData,
            project: quarterData.project.filter(project => {
              // When events are selected, ONLY check if this event is in the selected events
              // Do NOT apply search or other filters (event filter supersedes all)
              return selectedEvents.selectedEvents.includes(project.issueId);
            })
          }))
          .filter(quarterData => quarterData.project.length > 0)
      };

      // Collect all related implementation IDs
      const relatedIssues: Set<string> = new Set();
      const parentToUnnest: Set<string> = new Set();
      const matchedFeatures: Set<string> = new Set(); // Track features that match surface filter
      const matchedParents: Set<string> = new Set(); // Track parents that match surface/genai filters
      const matchedGenAiFeatures: Set<string> = new Set(); // Track features that match Gen AI filter
      const surfaceMatchedParents: Set<string> = new Set(); // ✅ Separate set for surface-matched parents
      const genAiMatchedParents: Set<string> = new Set(); // ✅ Separate set for genAI-matched parents

      events.projects.forEach(quarterData => {
        quarterData.project.forEach(project => {
          if (selectedEvents.selectedEvents.includes(project.issueId)) {
            // Use releaseHierarchyPaths from relationship object if available
            if (
              project.relationship?.releaseHierarchyPaths &&
              project.relationship.releaseHierarchyPaths.length > 0
            ) {
              project.relationship.releaseHierarchyPaths.forEach(path => {
                if (path.issue) {
                  // Prefer phaseId (UUID) over phase name for more accurate matching
                  if (path.phaseId) {
                    // Create composite key with UUID: issueId-phaseId-showOnTimeline
                    const compositeKey = `${path.issue}-${path.phaseId}-false`;
                    relatedIssues.add(compositeKey);
                  } else if (path.phase) {
                    // Fallback to phase name if phaseId not available
                    const compositeKey = `${path.issue}-${path.phase}-false`;
                    relatedIssues.add(compositeKey);
                  } else {
                    // No phase specified, add simple issueId to show all phases
                    relatedIssues.add(path.issue);
                  }

                  // If this is a child, unnest its parent
                  if (path.parent && path.parent !== project.issueId) {
                    parentToUnnest.add(path.parent);
                  }
                }
              });
            }
          }
        });
      });

      // ⭐ NEW: If surfaces are also selected, find matching features within event-related items
      if (selectedFeatures.selectedFeatures.length > 0) {
        // Scan through event-related items to find features with matching surfaces
        filteredReleasesData.forEach(release => {
          release.projects.forEach(project => {
            project.project.forEach(subProject => {
              // Only process if this item is event-related
              const isEventRelated =
                relatedIssues.has(subProject.issueId) ||
                (subProject.releasePhaseId &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhaseId}-false`
                  )) ||
                (subProject.releasePhase &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhase}-false`
                  ));

              if (isEventRelated) {
                // ✅ FIX: Check if parent itself has matching surfaces
                // When both Surface and GenAI filters are active, parent must match BOTH
                if (
                  Array.isArray(subProject.surfaces) &&
                  subProject.surfaces.length > 0
                ) {
                  const hasMatchingSurface = subProject.surfaces.some(surface =>
                    selectedFeatures.selectedFeatures.includes(surface)
                  );

                  if (hasMatchingSurface) {
                    const hasBothFilters =
                      genAiFilter.selectedFilters.length > 0;

                    if (hasBothFilters) {
                      // Both filters active - only add parent if it also matches GenAI
                      const parentMatchesGenAi =
                        (genAiFilter.selectedFilters.includes(
                          'gen-ai-all-features'
                        ) &&
                          subProject.genai === true) ||
                        (genAiFilter.selectedFilters.includes(
                          'gen-ai-consumes-credits'
                        ) &&
                          subProject.genai === true &&
                          subProject.consumesCredit === true);

                      if (parentMatchesGenAi) {
                        surfaceMatchedParents.add(subProject.issueId);
                        matchedParents.add(subProject.issueId);
                      }
                    } else {
                      // Only surface filter active
                      surfaceMatchedParents.add(subProject.issueId);
                      matchedParents.add(subProject.issueId);
                    }
                  }
                }

                // ✅ FIX: Check features within this event-related item
                // When both Surface and GenAI filters are active, feature must match BOTH
                if (
                  Array.isArray(subProject.features) &&
                  subProject.features.length > 0
                ) {
                  subProject.features.forEach(feature => {
                    if (Array.isArray(feature.surfaces)) {
                      const hasMatchingSurface = feature.surfaces.some(
                        surface =>
                          selectedFeatures.selectedFeatures.includes(surface)
                      );

                      if (hasMatchingSurface) {
                        const hasBothFilters =
                          genAiFilter.selectedFilters.length > 0;

                        if (hasBothFilters) {
                          // Both filters active - only add if feature also matches GenAI
                          const matchesGenAi =
                            (genAiFilter.selectedFilters.includes(
                              'gen-ai-all-features'
                            ) &&
                              feature.genai === true) ||
                            (genAiFilter.selectedFilters.includes(
                              'gen-ai-consumes-credits'
                            ) &&
                              feature.genai === true &&
                              feature.consumesCredit === true);

                          if (matchesGenAi) {
                            const featureUniqueId =
                              feature.featureId || feature.issueId;
                            if (featureUniqueId) {
                              matchedFeatures.add(featureUniqueId);
                              matchedGenAiFeatures.add(featureUniqueId); // Add to both sets for intersection check
                              parentToUnnest.add(subProject.issueId);
                              surfaceMatchedParents.add(subProject.issueId);
                            }
                          }
                        } else {
                          // Only Surface filter active
                          const featureUniqueId =
                            feature.featureId || feature.issueId;
                          if (featureUniqueId) {
                            matchedFeatures.add(featureUniqueId);
                            parentToUnnest.add(subProject.issueId);
                            surfaceMatchedParents.add(subProject.issueId);
                          }
                        }
                      }
                    }
                  });
                }
              }
            });
          });
        });
      }

      // ⭐ NEW: If Gen AI filter is also selected, find matching features within event-related items
      if (genAiFilter.selectedFilters.length > 0) {
        // Scan through event-related items to find Gen AI features
        filteredReleasesData.forEach(release => {
          release.projects.forEach(project => {
            project.project.forEach(subProject => {
              // Only process if this item is event-related
              const isEventRelated =
                relatedIssues.has(subProject.issueId) ||
                (subProject.releasePhaseId &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhaseId}-false`
                  )) ||
                (subProject.releasePhase &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhase}-false`
                  ));

              if (isEventRelated) {
                // ✅ FIX: Check if parent itself matches GenAI
                // When both Surface and GenAI filters are active, parent must match BOTH
                let parentMatchesGenAi = false;

                if (
                  genAiFilter.selectedFilters.includes('gen-ai-all-features')
                ) {
                  parentMatchesGenAi = subProject.genai === true;
                } else if (
                  genAiFilter.selectedFilters.includes(
                    'gen-ai-consumes-credits'
                  )
                ) {
                  parentMatchesGenAi =
                    subProject.genai === true &&
                    subProject.consumesCredit === true;
                }

                if (parentMatchesGenAi) {
                  const hasBothFilters =
                    selectedFeatures.selectedFeatures.length > 0;

                  if (hasBothFilters) {
                    // Both filters active - only add parent if it also matches Surface
                    const parentMatchesSurface =
                      Array.isArray(subProject.surfaces) &&
                      subProject.surfaces.length > 0 &&
                      subProject.surfaces.some(surface =>
                        selectedFeatures.selectedFeatures.includes(surface)
                      );

                    if (parentMatchesSurface) {
                      genAiMatchedParents.add(subProject.issueId);
                      matchedParents.add(subProject.issueId);
                    }
                  } else {
                    // Only GenAI filter active
                    genAiMatchedParents.add(subProject.issueId);
                    matchedParents.add(subProject.issueId);
                  }
                }

                // ✅ FIX: Check features within this event-related item for Gen AI
                // When both Surface and GenAI filters are active, feature must match BOTH
                if (
                  Array.isArray(subProject.features) &&
                  subProject.features.length > 0
                ) {
                  subProject.features.forEach(feature => {
                    let matchesGenAi = false;

                    if (
                      genAiFilter.selectedFilters.includes(
                        'gen-ai-all-features'
                      )
                    ) {
                      // Match any feature with genai = true
                      matchesGenAi = feature.genai === true;
                    } else if (
                      genAiFilter.selectedFilters.includes(
                        'gen-ai-consumes-credits'
                      )
                    ) {
                      // Match features with genai = true AND consumesCredit = true
                      matchesGenAi =
                        feature.genai === true &&
                        feature.consumesCredit === true;
                    }

                    if (matchesGenAi) {
                      const hasBothFilters =
                        selectedFeatures.selectedFeatures.length > 0;

                      if (hasBothFilters) {
                        // Both filters active - only add if feature also matches Surface
                        const matchesSurface =
                          Array.isArray(feature.surfaces) &&
                          feature.surfaces.some(surface =>
                            selectedFeatures.selectedFeatures.includes(surface)
                          );

                        if (matchesSurface) {
                          const featureUniqueId =
                            feature.featureId || feature.issueId;
                          if (featureUniqueId) {
                            matchedGenAiFeatures.add(featureUniqueId);
                            matchedFeatures.add(featureUniqueId); // Add to both sets for intersection check
                            parentToUnnest.add(subProject.issueId);
                            genAiMatchedParents.add(subProject.issueId);
                          }
                        }
                      } else {
                        // Only GenAI filter active
                        const featureUniqueId =
                          feature.featureId || feature.issueId;
                        if (featureUniqueId) {
                          matchedGenAiFeatures.add(featureUniqueId);
                          parentToUnnest.add(subProject.issueId);
                          genAiMatchedParents.add(subProject.issueId);
                        }
                      }
                    }
                  });
                }
              }
            });
          });
        });
      }

      // Optionally, you could use parentToUnnest to control UI state elsewhere
      // For now, just filter other releases to only show related implementations
      // ⭐ CASCADING FILTERS: Apply other filters (surfaces, products, etc.) ON TOP of event matches
      // This allows users to further narrow down event-related items
      processedReleases = filteredReleasesData
        .map(release => ({
          ...release,
          projects: release.projects
            .map(project => ({
              ...project,
              project: project.project.filter(subProject => {
                // If search is active, override events filter and show search results
                if (searchFilterActive && searchFilteredItems.length > 0) {
                  return true; // Let search filter handle the filtering, including child issues
                }

                // ⭐ STEP 1: Check if this card matches the EVENT filter
                const matchesSimpleId = relatedIssues.has(subProject.issueId);
                const matchesCompositeWithId =
                  subProject.releasePhaseId &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhaseId}-${
                      subProject.showOnTimeline || false
                    }`
                  );
                const matchesCompositeWithName =
                  subProject.releasePhase &&
                  relatedIssues.has(
                    `${subProject.issueId}-${subProject.releasePhase}-${
                      subProject.showOnTimeline || false
                    }`
                  );

                const matchesEvent =
                  matchesSimpleId ||
                  matchesCompositeWithId ||
                  matchesCompositeWithName;

                // If doesn't match event, filter it out
                if (!matchesEvent) {
                  return false;
                }

                // ⭐ STEP 2: Event matches! Now apply OTHER filters ON TOP (cascading)
                // In Must Wins view, audience filter is applied at swimlane level, not per-card
                const audienceMatch =
                  displayedMustWinView ||
                  audiences.selectedAudiences.length === 0 ||
                  (Array.isArray(subProject.customerAudiences)
                    ? subProject.customerAudiences.some(aud =>
                        audiences.selectedAudiences.includes(aud)
                      )
                    : audiences.selectedAudiences.includes(
                        subProject.customerAudiences
                      ));

                // Check product group filter
                const productGroupMatch =
                  productGroups.selectedGroups.length === 0 ||
                  (Array.isArray(subProject.productGroups)
                    ? subProject.productGroups.some(group =>
                        productGroups.selectedGroups.includes(group)
                      )
                    : productGroups.selectedGroups.includes(
                        subProject.productGroups
                      ));

                // Check category filter
                const categoryMatch =
                  categories.selectedCats.length === 0 ||
                  (Array.isArray(subProject.categories)
                    ? subProject.categories.some(cat =>
                        categories.selectedCats.includes(cat)
                      )
                    : categories.selectedCats.includes(subProject.categories));

                // Check surface/feature filter
                const surfaceMatch =
                  selectedFeatures.selectedFeatures.length === 0 ||
                  (() => {
                    // Check parent surfaces
                    const parentHasMatchingSurfaces =
                      Array.isArray(subProject.surfaces) &&
                      subProject.surfaces.length > 0 &&
                      subProject.surfaces.some(surface =>
                        selectedFeatures.selectedFeatures.includes(surface)
                      );

                    // Check feature surfaces
                    const featureHasMatchingSurfaces =
                      Array.isArray(subProject.features) &&
                      subProject.features.length > 0 &&
                      subProject.features.some(
                        feature =>
                          Array.isArray(feature.surfaces) &&
                          feature.surfaces.some(surface =>
                            selectedFeatures.selectedFeatures.includes(surface)
                          )
                      );

                    return (
                      parentHasMatchingSurfaces || featureHasMatchingSurfaces
                    );
                  })();

                // Check product filter
                const productMatch =
                  selectedProducts.selectedProducts.length === 0 ||
                  (Array.isArray(subProject.products)
                    ? subProject.products.some(prod =>
                        selectedProducts.selectedProducts.includes(prod)
                      )
                    : selectedProducts.selectedProducts.includes(
                        subProject.products
                      ));

                // Check revenue impact filter - apply to all cards
                const revenueImpactMatch =
                  !revenueImpactFilterActive ||
                  (() => {
                    const hasYesFilter =
                      revenueImpactFilter.selectedFilters.includes(
                        'revenue-impact-yes'
                      );
                    const hasNoFilter =
                      revenueImpactFilter.selectedFilters.includes(
                        'revenue-impact-no'
                      );
                    const revenueImpactValue =
                      subProject.revenueImpact === true;

                    // If both Yes and No are selected, show all (no filtering)
                    if (hasYesFilter && hasNoFilter) {
                      return true;
                    }

                    // If only Yes is selected, show only cards with revenueImpact === true
                    if (hasYesFilter && !hasNoFilter) {
                      return revenueImpactValue === true;
                    }

                    // If only No is selected, show only cards with revenueImpact === false
                    if (hasNoFilter && !hasYesFilter) {
                      return revenueImpactValue === false;
                    }

                    // If neither is selected (shouldn't happen if filter is active), show all
                    return true;
                  })();

                // ⭐ Return true if item matches event AND all other active filters
                // Note: GenAI is NOT checked here - it's handled by the dedicated GenAI highlighting section
                // This ensures parents with genai=true are not filtered out
                const finalMatch =
                  audienceMatch &&
                  productGroupMatch &&
                  categoryMatch &&
                  surfaceMatch &&
                  productMatch &&
                  revenueImpactMatch;

                return finalMatch;
              })
            }))
            .filter(project => project.project.length > 0)
        }))
        .filter(release => release.projects.length > 0);

      // ⭐ ALL FILTERS WORK TOGETHER WITH AND LOGIC
      // GenAI, Surface, and Events filters all apply together
      let finalProcessedReleases = processedReleases;
      const hasGenAiFilter = genAiFilter.selectedFilters.length > 0;
      const hasSurfaceFilter = selectedFeatures.selectedFeatures.length > 0;
      const hasEventsFilter = selectedEvents.selectedEvents.length > 0;

      if (hasGenAiFilter || hasSurfaceFilter) {
        // ✅ FIX: Use AND logic when multiple filters are active
        let itemsToShow: Set<string>;

        if (hasGenAiFilter && hasSurfaceFilter) {
          // BOTH filters active - show intersection (AND logic)
          itemsToShow = new Set(
            Array.from(genAiMatchedParents).filter(id =>
              surfaceMatchedParents.has(id)
            )
          );
        } else if (hasGenAiFilter) {
          // Only GenAI filter active
          itemsToShow = genAiMatchedParents;
        } else {
          // Only Surface filter active
          itemsToShow = surfaceMatchedParents;
        }

        // ⭐ IMPORTANT: Filter to show only matched items AND their matching features
        // If nothing matches, itemsToShow will be empty and filter will return nothing
        finalProcessedReleases = processedReleases
          .map(release => {
            // ✅ Filter features at release level
            let releaseFeatures = release.features;
            if (
              Array.isArray(release.features) &&
              release.features.length > 0
            ) {
              releaseFeatures = release.features.filter((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                return (
                  matchedFeatures.has(featureUniqueId) ||
                  matchedGenAiFeatures.has(featureUniqueId)
                );
              });
            }

            const filteredProjects = release.projects
              ?.map(project => {
                const filteredSubProjects = project.project
                  .filter(subProject => {
                    // First check if card matches GenAI/Surface filters
                    if (!itemsToShow.has(subProject.issueId)) {
                      return false;
                    }

                    // Then check revenue impact filter - apply to all cards
                    if (revenueImpactFilterActive) {
                      const hasYesFilter =
                        revenueImpactFilter.selectedFilters.includes(
                          'revenue-impact-yes'
                        );
                      const hasNoFilter =
                        revenueImpactFilter.selectedFilters.includes(
                          'revenue-impact-no'
                        );
                      const revenueImpactValue =
                        subProject.revenueImpact === true;

                      // If both Yes and No are selected, continue to check must win
                      if (!(hasYesFilter && hasNoFilter)) {
                        // If only Yes is selected, show only cards with revenueImpact === true
                        if (
                          hasYesFilter &&
                          !hasNoFilter &&
                          revenueImpactValue !== true
                        ) {
                          return false;
                        }

                        // If only No is selected, show only cards with revenueImpact === false
                        if (
                          hasNoFilter &&
                          !hasYesFilter &&
                          revenueImpactValue !== false
                        ) {
                          return false;
                        }
                      }
                    }

                    return true;
                  })
                  .map(subProject => {
                    // ✅ Filter features within each subProject
                    if (
                      Array.isArray(subProject.features) &&
                      subProject.features.length > 0
                    ) {
                      const filteredSubFeatures = subProject.features.filter(
                        (feature: any) => {
                          const featureUniqueId =
                            feature.featureId || feature.issueId;
                          return (
                            matchedFeatures.has(featureUniqueId) ||
                            matchedGenAiFeatures.has(featureUniqueId)
                          );
                        }
                      );

                      return {
                        ...subProject,
                        features: filteredSubFeatures
                      };
                    }
                    return subProject;
                  });

                return filteredSubProjects.length > 0
                  ? { ...project, project: filteredSubProjects }
                  : null;
              })
              .filter(Boolean);

            return filteredProjects?.length > 0 ||
              (releaseFeatures && releaseFeatures.length > 0)
              ? {
                  ...release,
                  features: releaseFeatures,
                  projects: filteredProjects
                }
              : null;
          })
          .filter(Boolean);
      }

      // Show Events row and only the rows containing related implementations
      const finalReleasesWithEvents: any[] = [];

      // Key Dates are handled by dedicated rendering section, never add to main array

      // Add processed events and releases
      if (processedEvents) {
        finalReleasesWithEvents.push(processedEvents);
      }
      finalReleasesWithEvents.push(...finalProcessedReleases);

      // Ensure selected lanes/tiers are shown even if empty
      ((): void => {
        const selectedTypesSet = new Set<string>(
          isAllSelected
            ? filterOptions
                .filter(opt => opt.key !== 'all')
                .map(opt => opt.key as string)
            : selectedTypes
        );
        // Map selected keys to their display labels (release.type)
        const selectedLabels = filterOptions
          .filter(opt => selectedTypesSet.has(opt.key as string))
          .map(opt => opt.label)
          .filter(label => label !== 'Key Dates' && !label.includes('Events'));

        const existingLabels = new Set<string>(
          finalReleasesWithEvents.map((r: any) => r?.type)
        );

        selectedLabels.forEach(label => {
          if (!existingLabels.has(label)) {
            finalReleasesWithEvents.push({
              type: label,
              projects: quarters.map(q => ({ quarter: q.label, project: [] }))
            });
          }
        });
      })();

      // ⭐ Dispatch highlighting
      if (
        selectedFeatures.selectedFeatures.length > 0 ||
        genAiFilter.selectedFilters.length > 0
      ) {
        // When GenAI/Surface filters are active, ONLY highlight matching items (not all event items)
        if (
          matchedFeatures.size > 0 ||
          matchedParents.size > 0 ||
          matchedGenAiFeatures.size > 0
        ) {
          const allHighlightedItems = [
            ...Array.from(matchedFeatures), // Surface-matched features
            ...Array.from(matchedParents), // Parents that match GenAI/surfaces
            ...Array.from(matchedGenAiFeatures) // GenAI-matched features
          ];

          dispatch(
            actions.updateHighlightedIssue({
              relatedIssues: allHighlightedItems,
              // Event filter expansion is governed by the global Un-Nest All
              // toggle, not selective per-parent unnesting. Passing [] keeps
              // every card out of "filter mode" so the global isUnNested flag
              // uniformly nests/un-nests all cards with children (highlighting
              // still comes from relatedIssues above).
              unnestParentIds: []
            })
          );
          dispatch(actions.savePreviousUnNestAllState());
        }
      } else {
        // If no GenAI/Surface filters, highlight all event-related items
        if (relatedIssues.size > 0) {
          dispatch(
            actions.updateHighlightedIssue({
              relatedIssues: Array.from(relatedIssues),
              // See note above: rely on the global Un-Nest All toggle for
              // event expansion so all cards nest/un-nest uniformly.
              unnestParentIds: []
            })
          );
          dispatch(actions.savePreviousUnNestAllState());
        }
      }

      // Sort releases by the defined order before dispatching
      dispatch(
        actions.setFilteredReleases(
          sortReleasesByOrder(finalReleasesWithEvents)
        )
      );
    }
    // If surfaces/features are selected, filter and unnest parents containing matching features
    // ⭐ BUT: Only if Events filter is NOT active (Events supersede GenAI/Surface)
    else if (
      (selectedFeatures.selectedFeatures.length > 0 ||
        genAiFilter.selectedFilters.length > 0) &&
      selectedEvents.selectedEvents.length === 0
    ) {
      console.log(
        '🔍 GenAI/Surface Filter Block 2: Applying GenAI/Surface filtering (no events active)',
        {
          surfaceFiltersLength: selectedFeatures.selectedFeatures.length,
          genAiFiltersLength: genAiFilter.selectedFilters.length,
          eventsLength: selectedEvents.selectedEvents.length,
          isUnNested // ✅ Log current unnest state
        }
      );
      const matchedFeatures: Set<string> = new Set();
      const matchedParents: Set<string> = new Set(); // Track parents that match surfaces
      const matchedGenAiFeatures: Set<string> = new Set(); // Track Gen AI features
      const parentToUnnest: Set<string> = new Set();
      const surfaceMatchedParents: Set<string> = new Set(); // ✅ Separate set for surface-matched parents
      const genAiMatchedParents: Set<string> = new Set(); // ✅ Separate set for genAI-matched parents

      // Scan all releases to find features with matching surfaces OR Gen AI
      filteredReleasesData.forEach(release => {
        // ✅ FIX: Check if release itself has matching surfaces (parent level)
        // When both filters are active, release must match BOTH
        if (Array.isArray(release.surfaces) && release.surfaces.length > 0) {
          const hasMatchingSurface = release.surfaces.some(surface =>
            selectedFeatures.selectedFeatures.includes(surface)
          );
          if (hasMatchingSurface) {
            const hasBothFilters = genAiFilter.selectedFilters.length > 0;

            if (hasBothFilters) {
              // Both filters active - only add if also matches GenAI
              const matchesGenAi =
                (genAiFilter.selectedFilters.includes('gen-ai-all-features') &&
                  release.genai === true) ||
                (genAiFilter.selectedFilters.includes(
                  'gen-ai-consumes-credits'
                ) &&
                  release.genai === true &&
                  release.consumesCredit === true);

              if (matchesGenAi) {
                surfaceMatchedParents.add(release.issueId);
                genAiMatchedParents.add(release.issueId);
                matchedParents.add(release.issueId);
              }
            } else {
              // Only surface filter active
              surfaceMatchedParents.add(release.issueId);
              matchedParents.add(release.issueId);
            }
          }
        }

        // ✅ FIX: Check if release itself matches Gen AI criteria (parent level)
        // When both filters are active, release must match BOTH
        if (genAiFilter.selectedFilters.length > 0) {
          let matchesGenAi = false;

          if (genAiFilter.selectedFilters.includes('gen-ai-all-features')) {
            matchesGenAi = release.genai === true;
          } else if (
            genAiFilter.selectedFilters.includes('gen-ai-consumes-credits')
          ) {
            matchesGenAi =
              release.genai === true && release.consumesCredit === true;
          }

          if (matchesGenAi) {
            const hasBothFilters = selectedFeatures.selectedFeatures.length > 0;

            if (hasBothFilters) {
              // Both filters active - only add if also matches Surface
              const matchesSurface =
                Array.isArray(release.surfaces) &&
                release.surfaces.length > 0 &&
                release.surfaces.some(surface =>
                  selectedFeatures.selectedFeatures.includes(surface)
                );

              if (matchesSurface) {
                genAiMatchedParents.add(release.issueId);
                surfaceMatchedParents.add(release.issueId);
                matchedParents.add(release.issueId);
              }
            } else {
              // Only GenAI filter active
              genAiMatchedParents.add(release.issueId);
              matchedParents.add(release.issueId);
            }
          }
        }

        // ✅ FIX: Check for features directly on the release object
        // When both filters are active, feature must match BOTH
        if (Array.isArray(release.features) && release.features.length > 0) {
          release.features.forEach(feature => {
            let matchesSurface = false;
            let matchesGenAi = false;

            // Check surface match
            if (
              selectedFeatures.selectedFeatures.length > 0 &&
              Array.isArray(feature.surfaces)
            ) {
              matchesSurface = feature.surfaces.some(surface =>
                selectedFeatures.selectedFeatures.includes(surface)
              );
            } else if (selectedFeatures.selectedFeatures.length === 0) {
              matchesSurface = true;
            }

            // Check GenAI match
            if (genAiFilter.selectedFilters.length > 0) {
              if (genAiFilter.selectedFilters.includes('gen-ai-all-features')) {
                matchesGenAi = feature.genai === true;
              } else if (
                genAiFilter.selectedFilters.includes('gen-ai-consumes-credits')
              ) {
                matchesGenAi =
                  feature.genai === true && feature.consumesCredit === true;
              }
            } else {
              matchesGenAi = true;
            }

            // ✅ Use AND logic
            if (matchesSurface && matchesGenAi) {
              const featureUniqueId = feature.featureId || feature.issueId;
              if (featureUniqueId) {
                if (selectedFeatures.selectedFeatures.length > 0) {
                  matchedFeatures.add(featureUniqueId);
                }
                if (genAiFilter.selectedFilters.length > 0) {
                  matchedGenAiFeatures.add(featureUniqueId);
                }
                parentToUnnest.add(release.issueId);
              }
            }
          });
        }

        // Also check for features nested within projects (for other moment types)
        if (Array.isArray(release.projects)) {
          release.projects.forEach(project => {
            project.project.forEach(subProject => {
              // ✅ FIX: Check if subProject itself has matching surfaces (parent level)
              // When both filters are active, subProject must match BOTH
              if (
                Array.isArray(subProject.surfaces) &&
                subProject.surfaces.length > 0
              ) {
                const hasMatchingSurface = subProject.surfaces.some(surface =>
                  selectedFeatures.selectedFeatures.includes(surface)
                );
                if (hasMatchingSurface) {
                  const hasBothFilters = genAiFilter.selectedFilters.length > 0;

                  if (hasBothFilters) {
                    // Both filters active - only add if also matches GenAI
                    const matchesGenAi =
                      (genAiFilter.selectedFilters.includes(
                        'gen-ai-all-features'
                      ) &&
                        subProject.genai === true) ||
                      (genAiFilter.selectedFilters.includes(
                        'gen-ai-consumes-credits'
                      ) &&
                        subProject.genai === true &&
                        subProject.consumesCredit === true);

                    if (matchesGenAi) {
                      surfaceMatchedParents.add(subProject.issueId);
                      genAiMatchedParents.add(subProject.issueId);
                      matchedParents.add(subProject.issueId);
                    }
                  } else {
                    // Only surface filter active
                    surfaceMatchedParents.add(subProject.issueId);
                    matchedParents.add(subProject.issueId);
                  }
                }
              }

              // ✅ FIX: Check if subProject itself matches Gen AI criteria (parent level)
              // When both filters are active, subProject must match BOTH
              if (genAiFilter.selectedFilters.length > 0) {
                let matchesGenAi = false;

                if (
                  genAiFilter.selectedFilters.includes('gen-ai-all-features')
                ) {
                  matchesGenAi = subProject.genai === true;
                } else if (
                  genAiFilter.selectedFilters.includes(
                    'gen-ai-consumes-credits'
                  )
                ) {
                  matchesGenAi =
                    subProject.genai === true &&
                    subProject.consumesCredit === true;
                }

                if (matchesGenAi) {
                  const hasBothFilters =
                    selectedFeatures.selectedFeatures.length > 0;

                  if (hasBothFilters) {
                    // Both filters active - only add if also matches Surface
                    const matchesSurface =
                      Array.isArray(subProject.surfaces) &&
                      subProject.surfaces.length > 0 &&
                      subProject.surfaces.some(surface =>
                        selectedFeatures.selectedFeatures.includes(surface)
                      );

                    if (matchesSurface) {
                      genAiMatchedParents.add(subProject.issueId);
                      surfaceMatchedParents.add(subProject.issueId);
                      matchedParents.add(subProject.issueId);
                    }
                  } else {
                    // Only GenAI filter active
                    genAiMatchedParents.add(subProject.issueId);
                    matchedParents.add(subProject.issueId);
                  }
                }
              }

              // Check if this subProject has features with matching surfaces OR Gen AI
              if (
                Array.isArray(subProject.features) &&
                subProject.features.length > 0
              ) {
                subProject.features.forEach(feature => {
                  // ✅ FIX: Track surface and GenAI matches separately for AND logic
                  let matchesSurface = false;
                  let matchesGenAi = false;

                  // Check surface match
                  if (
                    selectedFeatures.selectedFeatures.length > 0 &&
                    Array.isArray(feature.surfaces)
                  ) {
                    matchesSurface = feature.surfaces.some(surface =>
                      selectedFeatures.selectedFeatures.includes(surface)
                    );
                  } else if (selectedFeatures.selectedFeatures.length === 0) {
                    // No surface filter active - consider it a match
                    matchesSurface = true;
                  }

                  // Check Gen AI match
                  if (genAiFilter.selectedFilters.length > 0) {
                    if (
                      genAiFilter.selectedFilters.includes(
                        'gen-ai-all-features'
                      )
                    ) {
                      matchesGenAi = feature.genai === true;
                    } else if (
                      genAiFilter.selectedFilters.includes(
                        'gen-ai-consumes-credits'
                      )
                    ) {
                      matchesGenAi =
                        feature.genai === true &&
                        feature.consumesCredit === true;
                    }
                  } else {
                    // No GenAI filter active - consider it a match
                    matchesGenAi = true;
                  }

                  // ✅ Use AND logic: feature must match ALL active filters
                  const featureMatches = matchesSurface && matchesGenAi;

                  if (featureMatches) {
                    // Use featureId as the unique identifier
                    const featureUniqueId =
                      feature.featureId || feature.issueId;
                    if (featureUniqueId) {
                      // ✅ FIX: Only add to sets when BOTH filters match (for AND logic)
                      // Track which specific filters this feature matched
                      const hasBothFilters =
                        selectedFeatures.selectedFeatures.length > 0 &&
                        genAiFilter.selectedFilters.length > 0;

                      if (hasBothFilters) {
                        // Both filters active - only add if matches BOTH
                        if (matchesSurface && matchesGenAi) {
                          matchedGenAiFeatures.add(featureUniqueId);
                          matchedFeatures.add(featureUniqueId);
                          parentToUnnest.add(subProject.issueId);
                        }
                      } else {
                        // Only one filter active - add to appropriate set
                        if (
                          genAiFilter.selectedFilters.length > 0 &&
                          matchesGenAi
                        ) {
                          matchedGenAiFeatures.add(featureUniqueId);
                          parentToUnnest.add(subProject.issueId);
                        }
                        if (
                          selectedFeatures.selectedFeatures.length > 0 &&
                          matchesSurface
                        ) {
                          matchedFeatures.add(featureUniqueId);
                          parentToUnnest.add(subProject.issueId);
                        }
                      }
                    }
                  }
                });
              }
            });
          });
        }
      });

      // ⭐ Dispatch actions to highlight features (surface + Gen AI) AND matching parents
      if (
        matchedFeatures.size > 0 ||
        matchedParents.size > 0 ||
        matchedGenAiFeatures.size > 0
      ) {
        // ✅ FIX: Use AND logic when both filters are active
        const hasBothFilters =
          selectedFeatures.selectedFeatures.length > 0 &&
          genAiFilter.selectedFilters.length > 0;

        let featuresToHighlight: string[];
        let parentsToHighlight: string[];

        if (hasBothFilters) {
          // BOTH filters active - highlight only features/parents in BOTH sets (intersection)
          featuresToHighlight = Array.from(matchedFeatures).filter(id =>
            matchedGenAiFeatures.has(id)
          );
          parentsToHighlight = Array.from(surfaceMatchedParents).filter(id =>
            genAiMatchedParents.has(id)
          );
        } else {
          // Only one or no filters active - highlight all matched features/parents (union)
          featuresToHighlight = [
            ...Array.from(matchedFeatures),
            ...Array.from(matchedGenAiFeatures)
          ];
          parentsToHighlight = Array.from(matchedParents);
        }

        // Combine features and parents for highlighting
        const allHighlightedItems = [
          ...featuresToHighlight,
          ...parentsToHighlight
        ];

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allHighlightedItems,
            unnestParentIds: Array.from(parentToUnnest)
          })
        );
        dispatch(actions.savePreviousUnNestAllState());
        // Don't set global unNestAll - rely on selective expansion via unnestParentIds
      }

      // Filter releases to show only those with matching features OR matching parent surfaces
      processedReleases = filteredReleasesData
        .map(release => {
          // Check if release has features at its level (Product Releases with/without Marketing)
          const releaseHasMatchingFeatures = parentToUnnest.has(
            release.issueId
          );
          // ⭐ Check if release itself matches
          const releaseMatches = matchedParents.has(release.issueId);

          // ✅ FIX: Filter nested projects AND their features
          const filteredProjects = release.projects
            ?.map(project => ({
              ...project,
              project: project.project
                .filter(subProject => {
                  // ⭐ Keep subprojects that EITHER have matching features OR match themselves
                  return (
                    parentToUnnest.has(subProject.issueId) ||
                    matchedParents.has(subProject.issueId)
                  );
                })
                .map(subProject => {
                  // ✅ Filter features to show only matching ones
                  if (
                    Array.isArray(subProject.features) &&
                    subProject.features.length > 0
                  ) {
                    const filteredSubFeatures = subProject.features.filter(
                      (feature: any) => {
                        const featureUniqueId =
                          feature.featureId || feature.issueId;
                        // Show feature if it's in either matched set
                        return (
                          matchedFeatures.has(featureUniqueId) ||
                          matchedGenAiFeatures.has(featureUniqueId)
                        );
                      }
                    );

                    return {
                      ...subProject,
                      features: filteredSubFeatures
                    };
                  }
                  return subProject;
                })
            }))
            .filter(project => project.project.length > 0);

          // Include the release if:
          // 1. It has matching features at release level, OR
          // 2. It matches itself (has matching surfaces), OR
          // 3. It has nested projects with matching features
          const hasFilteredProjects =
            filteredProjects && filteredProjects.length > 0;

          if (
            releaseHasMatchingFeatures ||
            releaseMatches ||
            hasFilteredProjects
          ) {
            // ✅ FIX: Also filter features at the release level
            let releaseFeatures = release.features;
            if (
              Array.isArray(release.features) &&
              release.features.length > 0
            ) {
              releaseFeatures = release.features.filter((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                return (
                  matchedFeatures.has(featureUniqueId) ||
                  matchedGenAiFeatures.has(featureUniqueId)
                );
              });
            }

            return {
              ...release,
              // ✅ Filter features at release level
              features: releaseFeatures,
              // ⭐ Logic for keeping projects:
              // 1. If release has matching features at its level → keep original projects
              // 2. If release itself matches (parent matches GenAI/surfaces) → keep original projects (don't filter)
              // 3. Otherwise → use filtered projects (only matching features)
              projects:
                releaseHasMatchingFeatures || releaseMatches
                  ? release.projects
                  : filteredProjects
            };
          }

          return null;
        })
        .filter(Boolean); // Remove null releases

      const finalReleasesWithSurfaces: any[] = [];

      if (events) {
        finalReleasesWithSurfaces.push(events);
      }
      finalReleasesWithSurfaces.push(...processedReleases);

      // Ensure selected lanes/tiers are shown even if empty
      ((): void => {
        const selectedTypesSet = new Set<string>(
          isAllSelected
            ? filterOptions
                .filter(opt => opt.key !== 'all')
                .map(opt => opt.key as string)
            : selectedTypes
        );
        const selectedLabels = filterOptions
          .filter(opt => selectedTypesSet.has(opt.key as string))
          .map(opt => opt.label)
          .filter(label => label !== 'Key Dates' && !label.includes('Events'));

        const existingLabels = new Set<string>(
          finalReleasesWithSurfaces.map((r: any) => r?.type)
        );

        selectedLabels.forEach(label => {
          if (!existingLabels.has(label)) {
            finalReleasesWithSurfaces.push({
              type: label,
              projects: quarters.map(q => ({ quarter: q.label, project: [] }))
            });
          }
        });
      })();

      // Sort releases by the defined order before dispatching
      dispatch(
        actions.setFilteredReleases(
          sortReleasesByOrder(finalReleasesWithSurfaces)
        )
      );
    } else {
      // If no events, surfaces, or Gen AI filters selected, show all events and releases based on filter
      const finalReleases: any[] = [];

      // Key Dates are handled by dedicated rendering section, never add to main array

      // Add events if selected in filter
      if (events) {
        finalReleases.push(events);
      }

      // Add other releases
      finalReleases.push(...filteredReleasesData);

      // Ensure selected lanes/tiers are shown even if empty
      ((): void => {
        const selectedTypesSet = new Set<string>(
          isAllSelected
            ? filterOptions
                .filter(opt => opt.key !== 'all')
                .map(opt => opt.key as string)
            : selectedTypes
        );
        const selectedLabels = filterOptions
          .filter(opt => selectedTypesSet.has(opt.key as string))
          .map(opt => opt.label)
          .filter(label => label !== 'Key Dates' && !label.includes('Events'));

        const existingLabels = new Set<string>(
          finalReleases.map((r: any) => r?.type)
        );

        selectedLabels.forEach(label => {
          if (!existingLabels.has(label)) {
            finalReleases.push({
              type: label,
              projects: quarters.map(q => ({ quarter: q.label, project: [] }))
            });
          }
        });
      })();

      // ⚠️ IMPORTANT: DO NOT clear event highlights when slideout closes!
      // Only clear highlights if:
      // 1. No search is active AND
      // 2. No events are selected (from filter) AND
      // 3. No single-click highlights exist AND
      // 4. No search highlights exist AND
      // 5. Search filtered items is empty
      // This preserves filter-based, single-click, AND search event highlights
      const hasActiveEventFilter =
        selectedEvents.selectedEvents &&
        selectedEvents.selectedEvents.length > 0;
      const hasSingleClickHighlights =
        highlightedIssue && Object.keys(highlightedIssue).length > 0;
      const highlightSourceIsSingleClick = highlightSource === 'single-click';
      const highlightSourceIsSearch = highlightSource === 'search'; // ✅ Check for search highlights

      // ✅ ONLY clear if:
      // 1. No search filter active
      // 2. No event filter active (double-click)
      // 3. No single-click highlights (highlightSource !== 'single-click')
      // 4. No search highlights (highlightSource !== 'search')
      if (
        (!searchFilterActive || searchFilteredItems.length === 0) &&
        !hasActiveEventFilter &&
        !highlightSourceIsSingleClick &&
        !highlightSourceIsSearch // ✅ Also preserve search highlights
      ) {
        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: [],
            unnestParentIds: []
          })
        );
      } else {
      }

      // Sort releases by the defined order before dispatching
      const sortedReleases = sortReleasesByOrder(finalReleases);
      dispatch(actions.setFilteredReleases(sortedReleases));
    }

    setKeyDatesRelease(keyDates);

    // Mark initial load as complete after first successful data processing
    if (releases?.releaseScheduled && releases.releaseScheduled.length > 0) {
      initialLoadComplete.current = true;
    }
    // Performance logging (only in development)
    if (process.env.NODE_ENV === 'development') {
      const timeEnd = performance.now();
      if (timeEnd - timeStart > 100) {
        console.warn(`⚠️ Filtering took ${Math.round(timeEnd - timeStart)}ms`);
      }
    }
  }, [
    releases,
    categories.selectedCats,
    productGroups.selectedGroups,
    audiences.selectedAudiences,
    selectedEvents.selectedEvents,
    selectedFeatures.selectedFeatures,
    selectedProducts.selectedProducts,
    lanesTiers.selectedLanesTiers,
    status.selectedStatus,
    homeState.hideCompletedItems,
    momentTypeFilter.selectedMomentTypes,
    rsdMomentTypes,
    genAiFilter.selectedFilters,
    revenueImpactFilter.selectedFilters,
    filterOptions,
    searchFilterActive,
    searchFilteredItems,
    displayedMustWinView
    // ⚠️ DO NOT add highlightedIssue here - it would cause infinite loop
    // highlightedIssue changes trigger updateHighlightedIssue which changes highlightedIssue again
  ]);

  const getCurrentDatePosition = quarters => {
    const today = new Date();
    const currentMonth = today.toLocaleString('default', { month: 'short' });
    let currentMonthIndex = -1;
    let currentQuarterIndex = -1;
    const currentDate = today.getDate();
    quarters.forEach((quarter, quarterIndex) => {
      const monthIndex = quarter.months.findIndex(
        month => month === currentMonth
      );
      if (monthIndex !== -1) {
        currentQuarterIndex = quarterIndex;
        currentMonthIndex = quarterIndex * 3 + monthIndex; // Assuming each quarter has 3 months
      }
    });
    return { currentMonthIndex, currentQuarterIndex, currentDate };
  };

  // const currentMonthIndex = getCurrentDatePosition(quarters);
  useEffect(() => {
    setTodayDetails(getCurrentDatePosition(quarters));
  }, [quarters]);

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        {loading && (
          <Flex
            data-testid="roadmap-loader-container"
            UNSAFE_className={styles['loader-container']}
            justifyContent={'center'}
          >
            <Wait size="M" className="loader" />
          </Flex>
        )}
        {/* Alerts + grid share remaining viewport; grid row uses 1fr so sidebar/content scroll inside */}
        <div className={styles['roadmap-layout']}>
          <div className={styles['roadmap-alerts-slot']}>
            <AlertsContainer />
          </div>
          <Grid
            data-testid="roadmap-main-grid"
            UNSAFE_className={`${styles['roadmap-grid']} ${
              isFullScreenMode ? styles['fullscreen'] : ''
            }`}
            areas={
              showParkingLot ? ['sidebar content parking'] : ['sidebar content']
            }
            columns={
              showParkingLot
                ? [
                    isFullScreenMode
                      ? '0px'
                      : !isSidebarOpen
                      ? '40px'
                      : '160px',
                    '1fr',
                    `${PARKING_LOT_GRID_COLUMN_PX}px`
                  ]
                : [
                    isFullScreenMode
                      ? '0px'
                      : !isSidebarOpen
                      ? '40px'
                      : '160px',
                    '1fr',
                    '0px'
                  ]
            }
            rows={['minmax(0, 1fr)']}
          >
            {!isFullScreenMode && <LeftSidebar />}
            {/*middle grid */}
            <View
              data-testid="roadmap-content-area"
              gridArea="content"
              backgroundColor={'gray-50'}
              UNSAFE_className={`${styles['middle-grid']} ${
                isFullScreenMode ? styles['fullscreen'] : ''
              } ${showParkingLot ? styles['with-parking-lot'] : ''}`}
            >
              <Flex
                data-testid="roadmap-sticky-header"
                UNSAFE_style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  background: 'inherit',
                  minWidth: 'max-content'
                }}
              >
                <View
                  data-testid="roadmap-controls-section"
                  backgroundColor={'gray-50'}
                  UNSAFE_style={{
                    display: 'flex',
                    minWidth: '6rem',
                    position: 'sticky',
                    left: 0,
                    zIndex: 100,
                    justifyContent: 'flex-start',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #E8E8E8'
                  }}
                >
                  <Flex
                    data-testid="roadmap-control-buttons"
                    direction="row"
                    gap="4px"
                    UNSAFE_style={{
                      padding: '7px 12px 16px 12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      backgroundColor: 'white',
                      minHeight: '70px',
                      minWidth: displayedMustWinView ? '299px' : '6.2rem',
                      zIndex: 1
                    }}
                  >
                    {/* Collapse All with Tooltip */}
                    <TooltipTrigger
                      isOpen={openTooltip === 'collapse-all'}
                      onOpenChange={isOpen => {
                        if (!isOpen) setOpenTooltip(null);
                      }}
                      placement="top"
                    >
                      <div
                        onMouseEnter={() =>
                          handleTooltipMouseEnter('collapse-all')
                        }
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        <ActionButton
                          data-testid="roadmap-collapse-all-button"
                          isQuiet
                          onPress={() => {
                            exitCompactIfNeeded();
                            onCollapseAll();
                          }}
                          UNSAFE_style={{
                            cursor: 'pointer',
                            display: 'flex',
                            opacity: 1,
                            padding: 0,
                            background: 'none',
                            border: 'none',
                            boxShadow: 'none',
                            minWidth: 0,
                            minHeight: 0
                          }}
                        >
                          <img
                            src={isCollapsed ? collapseAllIcon : expandAllIcon}
                            alt={isCollapsed ? 'Collapse All' : 'Expand All'}
                            width={22}
                            height={22}
                          />
                        </ActionButton>
                      </div>
                      <Tooltip>
                        {isCollapsed ? 'Large Cards' : 'Small Cards'}
                      </Tooltip>
                    </TooltipTrigger>
                    {/* Un-Nest All with Tooltip */}
                    <TooltipTrigger
                      isOpen={openTooltip === 'unnest-all'}
                      onOpenChange={isOpen => {
                        if (!isOpen) setOpenTooltip(null);
                      }}
                      placement="top"
                    >
                      <div
                        onMouseEnter={() =>
                          handleTooltipMouseEnter('unnest-all')
                        }
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        <ActionButton
                          data-testid="roadmap-unnest-all-button"
                          isQuiet
                          isDisabled={false} // ✅ ENABLED: Allow user to control nesting freely
                          onPress={() => {
                            exitCompactIfNeeded();
                            const newUnNestedState = !isUnNested;
                            dispatch(actions.setUnNestAll(newUnNestedState));

                            // ✅ Clear manually expanded cards from sessionStorage when using global toggle
                            sessionStorage.removeItem(
                              'manually_expanded_cards'
                            );

                            // Save the unnest state preference to localStorage
                            const preferences = getUserPreferences();
                            saveUserPreferences({
                              ...preferences,
                              isUnNested: newUnNestedState
                            });
                          }}
                          UNSAFE_style={{
                            cursor: 'pointer', // ✅ ENABLED: Always show pointer cursor
                            display: 'flex',
                            opacity: 1, // ✅ ENABLED: Full opacity (not dimmed)
                            padding: 0,
                            background: 'none',
                            border: 'none',
                            boxShadow: 'none',
                            minWidth: 0,
                            minHeight: 0
                          }}
                        >
                          <img
                            src={isUnNested ? nestAllIcon : unNestAllIcon}
                            alt={isUnNested ? 'Un-Nest All' : 'Nest All'}
                            width={22}
                            height={22}
                          />
                        </ActionButton>
                      </div>
                      <Tooltip>
                        {isUnNested ? 'Nest All' : 'Un-Nest All'}
                      </Tooltip>
                    </TooltipTrigger>
                  </Flex>
                </View>

                <View
                  data-testid="roadmap-quarters-container"
                  UNSAFE_className={styles.quarterContainer}
                  UNSAFE_style={{
                    display: 'flex',
                    zIndex: 10,
                    borderBottom: '1px solid #E6E6E6'
                  }}
                >
                  {quarters.map((quarter, index) => (
                    <Flex
                      gap="size-125"
                      direction="column"
                      key={quarter.id}
                      width={
                        expandedQuarters.includes(quarter.label)
                          ? '65rem' // Increased from 61.25rem (980px) to 65rem (1040px) - 60px increase (3 cards × 20px each)
                          : '21.875rem' // Increased from 21.25rem (340px) to 21.875rem (350px) - 10px increase per quarter for compact
                      }
                      UNSAFE_style={{
                        textAlign: 'center',

                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Flex
                        alignItems="center"
                        gap="20px"
                        UNSAFE_className={styles.quarterHeading}
                      >
                        <Text
                          UNSAFE_style={{
                            fontSize: '1.25rem',
                            fontWeight: '500',
                            lineHeight: '100%',
                            letterSpacing: 0,
                            width: '44px',
                            height: '25px',
                            paddingTop: '1px'
                          }}
                        >
                          {quarter.displayLabel}
                        </Text>

                        <TooltipTrigger
                          isOpen={openTooltip === `quarter-${quarter.label}`}
                          onOpenChange={isOpen => {
                            if (!isOpen) setOpenTooltip(null);
                          }}
                          placement="top"
                        >
                          <div
                            onMouseEnter={() =>
                              handleTooltipMouseEnter(
                                `quarter-${quarter.label}`
                              )
                            }
                            onMouseLeave={handleTooltipMouseLeave}
                          >
                            <ActionButton
                              isQuiet
                              UNSAFE_style={{
                                cursor: 'pointer',
                                display: 'flex',
                                opacity: 1,
                                background: '#f8f8f8',
                                border: 'none',
                                boxShadow: 'none',
                                minWidth: 0,
                                minHeight: 0,
                                width: '23px',
                                height: '23px',
                                margin: '0 0.5rem'
                              }}
                            >
                              <a
                                className={styles.expandIcon}
                                onClick={() => onExpandQuarter(quarter.label)}
                                style={{ cursor: 'pointer' }}
                                data-testid={`roadmap-quarter-expand-${quarter.label
                                  .replace(/\s+/g, '-')
                                  .toLowerCase()}`}
                              >
                                {expandedQuarters.includes(quarter.label) ? (
                                  <img
                                    src={ExpandIcon}
                                    alt="Collapse Quarter"
                                  />
                                ) : (
                                  <img
                                    src={CollapseIcon}
                                    alt="Expand Quarter"
                                  />
                                )}
                              </a>
                            </ActionButton>
                          </div>
                          <Tooltip>
                            {expandedQuarters.includes(quarter.label)
                              ? 'Collapse Quarter'
                              : 'Expand Quarter'}
                          </Tooltip>
                        </TooltipTrigger>
                      </Flex>
                      <Grid
                        UNSAFE_className={styles.monthsHeader}
                        columns={`repeat(3, 1fr)`}
                      >
                        {quarter.months.map(month => (
                          <View
                            key={month}
                            borderEndWidth="thin"
                            UNSAFE_style={{
                              height: '18px',
                              textAlign: 'center',
                              color: '#b5b5b5',
                              borderRightColor:
                                month == 'Nov' ? '#FFFFFF' : '#E6E6E6'
                            }}
                          >
                            <Text
                              UNSAFE_className={styles.monthText}
                              UNSAFE_style={{}}
                            >
                              {month.toUpperCase()}
                            </Text>
                          </View>
                        ))}
                      </Grid>
                    </Flex>
                  ))}
                </View>

                <div
                  className={styles.whiteGapFiller}
                  style={{
                    width: `${gapWidth}px`
                  }}
                />
              </Flex>
              {/* Rows wrapper - ensures grid lines extend to the bottom of the viewport */}
              <div className={styles.rowsWrapper}>
                {/*
                  Must Wins always runs on the shared Roadmap dataset. When entering it from
                  My Stuff or a Saved View, exitActiveSavedView() triggers a refetch of that
                  dataset; until it resolves, filteredReleases still holds the previous view's
                  data. While that refetch is in flight, render nothing here rather than the
                  stale dataset — the existing roadmap-loader-container spinner above already
                  communicates the loading state.
                */}
                {myStuffHasNoCards ? (
                  <NoResults message="You don't have any cards where you're the Primary Contact." />
                ) : displayedMustWinView &&
                  loading ? null : displayedMustWinView ? (
                  // Show Must Wins view when toggle is ON (use filtered releases so filters apply); use deferred value so toggle feels instant
                  <MustWinsView
                    releases={{
                      releaseScheduled: filteredReleases || []
                    }}
                    quarters={quarters}
                    expandedQuarters={expandedQuarters}
                    gapWidth={gapWidth}
                  />
                ) : (
                  <div className={styles.timelineRows}>
                    {((searchFilterActive && searchFilteredItems.length > 0) ||
                      lanesTiers.selectedLanesTiers.includes('key-dates') ||
                      lanesTiers.selectedLanesTiers.includes('all')) &&
                      keyDatesRelease && (
                        <Flex>
                          <Row
                            isKeyDate={true}
                            release={keyDatesRelease}
                            quarters={quarters}
                            expandedQuarters={expandedQuarters}
                            gapWidth={gapWidth}
                          />
                        </Flex>
                      )}
                    {releasesToRender.length > 0 &&
                      releasesToRender.map(release => (
                        <Flex key={release.type} minHeight={'min-content'}>
                          <Row
                            release={release}
                            quarters={quarters}
                            expandedQuarters={expandedQuarters}
                            gapWidth={gapWidth}
                          />
                        </Flex>
                      ))}

                    {/* Grid line filler - extends quarter border lines to the bottom */}
                    <div className={styles.gridLineFiller}>
                      <div className={styles.gridLineFillerHeader} />
                      <div className={styles.gridLineFillerColumns}>
                        {quarters.map(quarter => (
                          <div
                            key={`filler-${quarter.id}`}
                            className={styles.gridLineFillerColumn}
                            style={{
                              width: expandedQuarters.includes(quarter.label)
                                ? '65rem'
                                : '21.875rem'
                            }}
                          />
                        ))}
                        <div
                          className={styles.gridLineFillerGap}
                          style={{
                            width: gapWidth ? `${gapWidth}px` : undefined
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </View>

            {showParkingLot && (
              <View
                gridArea="parking"
                UNSAFE_className={`${styles['parking-lot']} ${
                  isFullScreenMode ? styles['fullscreen'] : ''
                }`}
              >
                <ParkingLot
                  showParkingLot={showParkingLot}
                  data={releases.tbd}
                  isFullScreenMode={isFullScreenMode}
                />
              </View>
            )}

            {/* <TodayMarker /> */}
            <Legend />
          </Grid>
        </div>
      </Suspense>
    </>
  );
};

export default Roadmap;
